import { VEHICLES } from '../../data/vehicles';
import { DIFFICULTY_LEVELS } from '../../data/difficulty';
import { getFinanceSender } from '../helpers/financeSender';
import { CHALLENGES } from '../../data/challenges';
import { reconcileDriverContractStatus } from '../helpers/reconcileDriverContractStatus';
import { tryPromotePhase } from '../helpers/phaseProgression';
import { TAX_REGIMES } from '../../data/taxes';
import { getAbroadDailyCost, COMPETITORS } from '../../data/competitors';
import {
  defaultRelationsFor,
  clampRelationScore,
  driftRelationsTowardNeutral,
  RELATION_DELTAS,
} from '../../utils/competitorRelations';
import { calculateLoanPayment, isWorkingDay } from '../../utils/format';
import { generateId, generateAvailableContracts, generateSpecialContract, generateRandomEvent, topUpAvailableContracts } from '../helpers';
import { generateTradeShowOffers } from '../../data/tradeShow';
import {
  getDriverFatigueMultiplier,
  getDepotMonthlyReputationBonus,
  isForceInvestActive,
  FORCE_INVEST_REPUTATION_PENALTY,
} from '../../data/depotEconomics';
import { getTraitDailyDelta } from '../../data/personalityTraits';
import { runDriverAlertsTick } from '../helpers/driverAlertsTick';
import { LEGAL_FORMS, getDefaultFiscalRegime } from '../../data/legalForms';
import { FISCAL_QUIZZES } from '../../data/fiscalQuizzes';
import { runLegalFormEvolutionTick } from '../helpers/legalFormEvolutionTick';
import { releaseDriversOnContractComplete } from '../helpers/releaseDriversOnContractComplete';
import { computeBalanceSheet } from '../../utils/balanceSheet';
import { runBankruptcyTick } from '../helpers/bankruptcyTick';
import { buildAchievementDefs } from '../helpers/achievements';
import { buildMonthlyReport } from '../helpers/monthlyReportBuilder';
import { buildAnnualReport } from '../helpers/annualReportBuilder';
import { buildFleetQuarterMessage, applyDriverDailyActivity } from '../helpers/fleetReportBuilder';
import { rollCompetitionEvent } from '../helpers/competitionEvents';
import { buildDafMonthlyReport } from '../helpers/dafMonthlyReport';
import { buildSupportYearlyDigestMessages } from '../helpers/supportYearlyDigestTick';
import { buildMidYearReview } from '../helpers/midYearReviewBuilder';
import { buildNathalieDailyMessages } from '../helpers/nathalieDailyTick';
import { runDrealFineTick } from '../helpers/drealFineTick';
import { runHseComplianceTick } from '../helpers/hseComplianceTick';
import { buildAnnualEcoVadisAudit, getEcoVadisReputationBonus } from '../helpers/ecoVadisAudit';
import { computeDailyRevenue } from '../helpers/computeDailyRevenue';
import { computeDailyExpenses } from '../helpers/computeDailyExpenses';
import { scaleImageGain, monthlyReputationErosion, monthlyNotorietyErosion } from '../helpers/imageGainCurve';
import { applyStrikeModifier } from '../helpers/applyStrikeModifier';
import { computeDailyAgios } from '../helpers/overdraftHelper';
import { runDriverFatigueTick } from '../helpers/driverFatigueTick';
import { _processDepartureNotices } from '../helpers/driverDepartureTick';
import { getInterimDailyCost } from '../helpers/driverHolidayTick';
// 10/07/2026 (P2 dette technique) — cycle des congés extrait de nextDay.
import { runHolidayCycleTick } from '../helpers/holidayCycleTick';
import { tickContractsLifecycle } from '../helpers/tickContractsLifecycle';
import { applyMarketEquilibrium, appendMarginHistory, MARKET_EQUILIBRIUM_MAX_GROSS_MARGIN } from '../helpers/marketEquilibrium';
import { tickVehicles } from '../helpers/tickVehicles';
import { applyPendingLegalFormChange } from '../helpers/applyPendingLegalFormChange';
import { runRosterGuardTick } from '../helpers/rosterGuardTick';
import { buildDefaultGameState } from '../helpers/resetGameState';
import { buildLegalFormUpgrade } from '../helpers/legalFormUpgrade';
import { runMonthlyCreditAmortization } from '../helpers/monthlyCreditAmortization';
import { computeMonthlyLeasingRepPenalty } from '../helpers/monthlyLeasingRepPenalty';

const getDiff = (state) => DIFFICULTY_LEVELS[state.difficulty] || DIFFICULTY_LEVELS.normal;

export const createCoreSlice = (set, get) => ({
  gameStarted: false,
  // 20/07/2026 — temps de jeu réel cumulé (secondes), remonté en
  // supervision. Incrémenté par useTelemetryReporter (30 s si onglet visible).
  playTimeSeconds: 0,
  company: null,
  difficulty: 'normal',
  day: 1,
  month: 1,
  year: 1,
  totalDays: 0,
  treasury: 0,
  reputation: 50,
  notoriety: 10,
  phase: 1,
  // Pedagogical objectives currently tracked for this apprentice (set by
  // useObjectivesPoller from /api/master/objectives/by-apprentice).
  pedagogicalObjectives: [],
  // Fiscal threshold quiz that pops as a modal when triggered.
  pendingFiscalQuiz: null,

  // ============== BANKRUPTCY / GAME OVER (fin de partie) =================
  // Consecutive days where treasury < 0 AND equity < 0. At 30 → liquidation.
  insolvencyStreak: 0,
  // Current visible alert stage: null | 'amber' | 'rose' | 'red'.
  bankruptcyAlertStage: null,
  // Last day we pushed an alert messagerie entry, to debounce spam.
  bankruptcyAlertLastPushDay: 0,
  // True once the player has used their one-shot Assemblée Générale Extraordinaire.
  emergencyShareholderUsed: false,
  // 06/02/2026 — P1 retour user : cumul de l'apport personnel injecté par
  // le fondateur lors d'une recapitalisation d'urgence (acceptEmergencyRecap).
  // Diminue d'autant à chaque dividende versé (le fondateur est remboursé
  // par les dividendes avant que l'argent ne ressorte de l'entreprise).
  personalCapitalInjected: 0,
  // Pending AGE modal: { recapAmount, deficit, daysUntilLiquidation } | null.
  pendingShareholderMeeting: null,
  // Game over state — null until liquidation triggers. Shape:
  // { stage: 1|2|3, trigger: 'cessation'|'technical', liquidationDay, summary: {...} }
  gameOverState: null,
  // Pedagogical hand-off to the next game (consumed by createCompany):
  // [{ cause, tip }, ...] from buildGameOverSummary.
  wisdomTipsForNextGame: null,

  // ============== RELATIONS CONCURRENTIELLES (Phase 1) ===================
  // 17/06/2026 — Score 0-100 par concurrent. 50 = neutre (défaut).
  // Phase 1 : lecture/affichage. Phases 2-5 : drift, événements OPA,
  // sous-traitance, débauchage. Hydraté à la création de société (cf.
  // createCompany dans cette même slice) pour les 4 concurrents par
  // défaut, et étendu pour tout nouveau concurrent injecté côté tenders.
  competitorRelations: {},

  // 12/02/2026 — Seuils marketScale déjà franchis (message presse envoyé).
  // Cf. store/helpers/marketScaleNarrative.js.
  marketScaleFiredThresholds: [],
  // 12/02/2026 — Fenêtre "concurrence renforcée" (rétro-effet du narratif).
  // Jusqu'à ce jour, les concurrents sont plus agressifs sur les AO.
  competitorAggressionUntilDay: 0,

  // ============== STRIKE (events grève) =================================
  // Active strike modifier from evt-social-01 / evt-social-03. Shape:
  // { kind: 'national' | 'blocking', label, revenuePenalty: 0..1, until: day }
  // Auto-cleared in nextDay when totalDays > until.
  companyStrike: null,

  // 08/02/2026 — Q2 Lot D : boom sectoriel actif (event evt-marche-03 choix
  // « Saisir »). Shape : { theme, label, startDay, endDay, revenueBoost }.
  // Auto-clearé en nextDay quand totalDays > endDay. Boost +20 % sur les
  // contrats matchant le secteur. Widget Dashboard affiche le countdown.
  marketBoom: null,

  // ============== NAV "NEW" BADGES ======================================
  // Counts of newly-unlocked features per nav path. Incremented when a
  // support hire unlocks a flag (mapped via supportSlice.UNLOCK_TO_NAV)
  // OR when a pedagogical milestone is reached (first M&A opportunity,
  // first DREAL inspection, first strike, phase upgrade, etc.). Cleared
  // when the user navigates to that path. Shape: { '/fiscalite': 2 }.
  newFeatureCounts: {},
  // List of milestone IDs already triggered — prevents the same badge from
  // being bumped twice per game. Reset by `resetGame`.
  milestonesSeen: [],

  // 08/02/2026 — Q2 P2 : tooltip pédagogique sur la 1ère négo ratée.
  // Set à true après le premier préavis envoyé pour ne plus afficher
  // l'encadré explicatif sur les préavis suivants.
  seenFirstRenegoFailure: false,

  // ============== MILESTONE VIDEO EVENT (Sora 2 fullscreen modal) =======
  // 08/02/2026 — Q2 narrative layer : when a major gameplay milestone fires
  // (vehicle Tier 2+, depot opening, competitor buyout, AO national win,
  // office upgrade, game over, phase up), the orchestrator slice pushes a
  // payload here and `MilestoneVideoModal` (mounted in Layout.jsx) plays
  // a 4 s Sora 2 clip + caption. Shape : { kind: string, reason?: string,
  // phase?: number }. Consumed by `consumeLastMilestoneEvent`.
  lastMilestoneEvent: null,

  triggerMilestoneVideo: (payload) => {
    if (!payload || !payload.kind) return;
    set({ lastMilestoneEvent: { ...payload, _t: Date.now() } });
  },

  consumeLastMilestoneEvent: () => {
    const state = get();
    if (state.lastMilestoneEvent) set({ lastMilestoneEvent: null });
  },

  // ============== HIDDEN CONTRACTS ======================================
  // 30/05/2026 — players can dismiss available contracts via an X button
  // to reduce noise. Hidden contracts disappear from the dashboard until
  // the player clicks "Restore N hidden contracts" — they then return to
  // the visible feed. Reset on resetGame.
  hiddenContractIds: [],

  hideAvailableContract: (contractId) => {
    if (!contractId) return;
    set((state) => {
      const cur = state.hiddenContractIds || [];
      if (cur.includes(contractId)) return {};
      return { hiddenContractIds: [...cur, contractId] };
    });
  },

  restoreHiddenContracts: () => {
    set({ hiddenContractIds: [] });
  },

  setPedagogicalObjectives: (items) => set({ pedagogicalObjectives: Array.isArray(items) ? items : [] }),

  // 05/02/2026 (Q1 engagement) — Toggle for juicy AO result sound effects.
  setAoSoundEnabled: (enabled) => set({ aoSoundEnabled: !!enabled }),

  /**
   * Answer the pending pedagogical fiscal quiz. Marks it as passed so it
   * doesn't fire again. If the answer is correct → reputation bonus, msg
   * "Bravo". If wrong → no bonus, explanatory msg. The pending legal-form
   * change remains scheduled either way.
   */
  answerFiscalQuiz: (optionId) => {
    const state = get();
    const pending = state.pendingFiscalQuiz;
    if (!pending) return;
    const quiz = FISCAL_QUIZZES[pending.quizId];
    if (!quiz) {
      set({ pendingFiscalQuiz: null });
      return;
    }
    const option = quiz.options.find(o => o.id === optionId);
    const isCorrect = !!option?.correct;
    // Reputation bump only on a correct answer.
    const reputationDelta = isCorrect ? (quiz.correctReputationBonus || 0) : 0;
    // Persist that this quiz has been answered (so it doesn't pop again).
    const newCompany = {
      ...state.company,
      fiscalQuizPassed: [
        ...((state.company?.fiscalQuizPassed) || []),
        pending.quizId,
      ],
    };
    set({
      pendingFiscalQuiz: null,
      company: newCompany,
      reputation: Math.max(0, Math.min(100, (state.reputation || 0) + reputationDelta)),
    });
    // Push a feedback message to the inbox.
    try {
      const pushMsg = get().pushCustomMessage;
      if (pushMsg && option) {
        pushMsg({
          senderType: 'urssaf',
          senderName: quiz.sender,
          tone: isCorrect ? 'friendly' : 'formal',
          kind: isCorrect ? 'info' : 'warning',
          subject: isCorrect ? '✓ Bonne réponse à l\'examen fiscal' : 'Réponse incorrecte — explication',
          body: `${option.explanation}\n\n${isCorrect ? `+${quiz.correctReputationBonus} points de réputation pour votre maîtrise du sujet.` : 'La bascule de statut/régime se fera quand même au 01/01, mais sans bonus pédagogique.'}`,
          ttlDays: 21,
        });
      }
    } catch (err) { console.warn('[hst-store] silent failure:', err); }
  },

  // ============= BANKRUPTCY ACTIONS =============

  /**
   * Accept the emergency recapitalization via Assemblée Générale Extraordinaire.
   * The player injects `recapAmount` (perso) into the treasury, resets the
   * insolvency streak, and burns their one-shot AGE. Returns true on success.
   */
  // 06/02/2026 — P2 retour user : impacts réels des news diesel et grève péages.
  // `fuelPriceModifier` est un multiplicateur (>1 pour hausse, <1 pour baisse)
  // appliqué à la conso carburant quotidienne pendant `expiresOnDay - totalDays`
  // jours. Source : 'fuel-up' | 'fuel-down' (news-fuel-up-01 / news-fuel-down-01).
  fuelPriceModifier: null,
  // 08/02/2026 — Q2 Lot B user prod : booster de pannes (Canicule, Verglas
  // continuer prudemment). Si > totalDays, computeBreakdownRiskMultiplier
  // multiplie la proba par 1.25.
  breakdownBoostUntil: 0,
  // `peageStrike` : surcoût quotidien fixe (détours, peages bloqués) + impact
  // léger sur la satisfaction des contrats actifs. Pose par news-strike-01.
  peageStrike: null,
  acceptEmergencyRecap: () => {
    const state = get();
    const pending = state.pendingShareholderMeeting;
    if (!pending || state.emergencyShareholderUsed) return false;
    const amount = pending.recapAmount;
    set({
      treasury: (state.treasury || 0) + amount,
      insolvencyStreak: 0,
      bankruptcyAlertStage: null,
      emergencyShareholderUsed: true,
      pendingShareholderMeeting: null,
      personalCapitalInjected: (state.personalCapitalInjected || 0) + amount,
    });
    // Push a confirmation message into the inbox.
    try {
      const pushMsg = get().pushCustomMessage;
      if (pushMsg) {
        pushMsg({
          senderType: 'bank',
          senderName: 'Notaire — Assemblée Générale',
          tone: 'formal',
          kind: 'info',
          subject: 'Recapitalisation enregistrée',
          body: `Apport personnel de ${amount.toLocaleString('fr-FR')} € enregistré au capital social. Votre entreprise reprend ses opérations normalement. Attention : cette aide ne peut être utilisée qu'une seule fois par partie.`,
          ttlDays: 30,
        });
      }
    } catch (err) { console.warn('[hst-store] silent failure:', err); }
    return true;
  },

  /**
   * Decline the AGE. The modal closes but the alert remains. Liquidation
   * will still happen if the player can't recover within the streak budget.
   */
  declineEmergencyRecap: () => set({ pendingShareholderMeeting: null }),

  /**
   * Advance the GameOverModal across its 3 acts (Convocation → Liquidation → Bilan).
   */
  advanceGameOverStage: () => {
    const state = get();
    if (!state.gameOverState) return;
    const next = Math.min(3, (state.gameOverState.stage || 1) + 1);
    set({ gameOverState: { ...state.gameOverState, stage: next } });
  },

  /**
   * Close the GameOverModal and trigger a brand-new game. The wisdom tips
   * from the failed run are persisted across the reset so Maurice can deliver
   * them in the new game's welcome message (see createCompany).
   */
  restartAfterLiquidation: () => {
    const state = get();
    const tips = state.gameOverState?.summary?.tips || null;
    const causes = state.gameOverState?.summary?.causes || null;
    // Call full reset.
    if (typeof get().resetGame === 'function') get().resetGame();
    // Persist wisdom for the next createCompany.
    if (tips && causes) {
      set({
        wisdomTipsForNextGame: tips.map((tip, i) => ({ tip, cause: causes[i] })),
      });
    }
  },


  /**
   * Manual upgrade of the company legal form. Charges the capital social
   * difference (if any) and records the change in the company history.
   * Returns false if the upgrade cannot be applied (insufficient treasury,
   * non-monotonic upgrade…).
   */
  upgradeLegalForm: (targetForm) => {
    const state = get();
    // 11/02/2026 — Pure logic extracted to helpers/legalFormUpgrade
    // (refactor coreSlice >2700 lignes).
    const result = buildLegalFormUpgrade(state, targetForm, {
      LEGAL_FORMS,
      getDefaultFiscalRegime,
    });

    if (!result.ok) {
      // Capital insuffisant → on remonte le mémo au joueur.
      if (result.reason === 'insufficient_capital' && result.message) {
        try {
          const pushMsg = get().pushCustomMessage;
          if (pushMsg) pushMsg(result.message);
        } catch (err) { console.warn('[hst-store] silent failure:', err); }
      }
      return false;
    }

    set(result.patch);
    try {
      const pushMsg = get().pushCustomMessage;
      if (pushMsg) pushMsg(result.message);
    } catch (err) { console.warn('[hst-store] silent failure:', err); }
    return true;
  },

  createCompany: ({ name, logo, customLogo, city, specialty, difficulty = 'normal', advisor = null, legalForm = 'ei' }) => {
    // Reset cross-slice persisted state from any previous game so a fresh
    // career starts with a clean fiscal/banking/support situation. Previously
    // VAT due, IS payable, bank loyalty etc. leaked across "Quitter la partie"
    // → "Nouvelle partie" sequences.
    // 12/02/2026 (fix user prod « arrivé en avril les anciens chauffeurs
    // apparaissent ») — Défense en profondeur : on force un reset pristine
    // au démarrage d'une nouvelle boîte, même si `resetGame()` a déjà
    // tourné (protège aussi le cas où le user arrive de la landing page
    // directement sans passer par Quitter).
    if (typeof get().resetTaxState === 'function') get().resetTaxState();
    const { getPristineStore } = require('../gameStorePristine');
    const pristine = getPristineStore();
    if (pristine) {
      const currentState = get();
      // On PRÉSERVE `wisdomTipsForNextGame` : Maurice doit pouvoir livrer
      // les conseils issus de la partie précédente (cf. restartAfterLiquidation).
      const preservedWisdom = currentState.wisdomTipsForNextGame;
      const cleared = {};
      for (const k of Object.keys(currentState)) {
        if (typeof currentState[k] !== 'function') cleared[k] = undefined;
      }
      set(cleared);
      const pristineData = {};
      for (const k of Object.keys(pristine)) {
        if (typeof pristine[k] !== 'function') pristineData[k] = pristine[k];
      }
      set(pristineData);
      if (preservedWisdom) set({ wisdomTipsForNextGame: preservedWisdom });
    }
    set({
      // Bank slice
      // 10/02/2026 (Lot J) — Retour user prod : « Étant donné que l'on commence
      // avec un contrat d'emprunt à la BNB Mariba, nous ne devrions pas être
      // considéré nouveau client. » On initialise la relation BNB Mariba à
      // partir du crédit fondateur (totalBorrowed = loanAmount, activeLoanCount
      // = 1, firstLoanDay = 0, loyaltyLevel = 1). Les autres banques restent
      // à zéro car aucune relation préalable.
      bankRelations: {
        bnb_mariba: { loansRepaid: 0, totalBorrowed: 0, activeLoanCount: 0, firstLoanDay: null, loyaltyLevel: 0 },
        societe_generaliste: { loansRepaid: 0, totalBorrowed: 0, activeLoanCount: 0, firstLoanDay: null, loyaltyLevel: 0 },
        credit_agricool: { loansRepaid: 0, totalBorrowed: 0, activeLoanCount: 0, firstLoanDay: null, loyaltyLevel: 0 },
      },
      activePlacements: [],
      pendingProposals: null,
      creditScore: null,
      scoreRevealed: false,
      scoreRevealMessage: null,
      scoreRevealDismissed: false,
      // Support slice
      hiredSupport: {},
      // Misc cross-slice safety net
      notifications: [],
      completedChallenges: {},
      achievements: {},
      pendingPhaseUp: null,
      pendingMonthlyReport: null,
      tradeShowMonth: 0,
      // 05/02/2026 (Q1 Lot 2) — Reset salon marketing state.
      marketingSalon: null,
      pendingSalonInvitation: null,
      lastSalonProposalDay: 0,
      salonProspectTenders: 0,
      // 12/02/2026 (fix user prod « la flotte de chauffeurs a été
      // récupérée d'une partie précédente ») — Reset explicite du vivier
      // RH : sans ça, les candidats ajoutés par Nadia/Karim dans la
      // précédente partie restaient présélectionnés dans la nouvelle.
      driverPool: {
        availableIds: [],
        takenBy: {},
        lastTickDay: 0,
        lastJobFairDay: -999,
        jobAdActive: false,
        jobAdEndDay: 0,
      },
      driverPoolEvents: [],
    });

    const diff = DIFFICULTY_LEVELS[difficulty] || DIFFICULTY_LEVELS.normal;
    // Look up the chosen legal form to deduce capital social, fiscal regime and
    // initial business posture. Defaults fall back to EI if anything looks wrong.
    const legalFormDef = LEGAL_FORMS[legalForm] || LEGAL_FORMS.ei;
    const initialFiscalRegime = getDefaultFiscalRegime(legalForm);
    const capitalSocial = legalFormDef.capitalRecommended || legalFormDef.capitalMin || 0;
    const initialLoan = {
      id: generateId(),
      bankId: 'bnb_mariba',
      origin: 'founder',        // distinguishes the startup credit from real bank loans
      label: 'Crédit fondateur',
      principal: diff.loanAmount,
      annualRate: 0.045,
      durationMonths: 36,
      monthlyPayment: Math.round(calculateLoanPayment(diff.loanAmount, 0.045, 36)),
      remainingMonths: 36,
      remainingPrincipal: diff.loanAmount,
      startDay: 0,
    };

    const firstVehicle = VEHICLES.find(v => v.id === 'valrek-maxor-l2h2');
    const ownedVehicle = {
      id: generateId(),
      dataId: firstVehicle.id,
      brand: firstVehicle.brand,
      model: firstVehicle.model,
      tier: firstVehicle.tier,
      image: firstVehicle.image || null,
      specialties: firstVehicle.specialties,
      price: firstVehicle.price,
      maintenancePerMonth: firstVehicle.maintenancePerMonth,
      insurancePerMonth: firstVehicle.insurancePerMonth,
      dailyFuel: firstVehicle.dailyFuel,
      wear: 0,
      km: 0,
      daysActive: 0,
      totalRevenue: 0,
      purchaseDay: 0,
      status: 'idle',
      assignedDriverId: null,
      assignedContractId: null,
      insuranceCoverage: 'standard',
    };

    const initialTreasury = diff.startingTreasury + diff.loanAmount - 22000 - capitalSocial;
    // Bootstrap client loyalty BEFORE generating contracts so each contract
    // is tagged with a real recurring-client id from day 1.
    const initialClientLoyalty = (typeof get().bootstrapClientLoyalty === 'function')
      ? (() => { get().bootstrapClientLoyalty(); return get().clientLoyalty; })()
      : {};
    const initialContracts = generateAvailableContracts(1, 1, 50, 1, {
      spawnDay: 0,
      clientLoyalty: initialClientLoyalty,
      // 03/06/2026 — passer un `state` initial pour activer le nouveau
      // calcul de cible (3-7 contrats au démarrage, gérant seul).
      state: { totalDays: 0, reputation: 50, notoriety: 0, hiredSupport: {} },
      // 13/06/2026 — Chantier Dépôts : géographie dès le jour 1 (le QG
      // n'est bootstrapé qu'après ce set, on passe donc un proxy).
      geo: {
        ownedDepots: [{ id: 'depot-hq', isHeadquarters: true, cityId: city }],
        company: { city },
      },
    });

    set({
      gameStarted: true,
      playTimeSeconds: 0,
      company: {
        name,
        logo,
        customLogo: customLogo || null,
        city,
        specialty,
        createdAt: new Date().toISOString(),
        legalForm,
        fiscalRegime: initialFiscalRegime,
        capitalSocial,
        legalFormHistory: [{
          from: null,
          to: legalForm,
          atDay: 0,
          reason: 'Création de la société',
        }],
        // Pending legal-form change planned for 01/01 next year (set by tax tick).
        pendingLegalFormChange: null,
      },
      difficulty,
      // 28/07/2026 — Fiche apprenant par partie : identifiant unique de la
      // partie, remonté en télémétrie (game_run_id).
      gameRunId: generateId(),
      day: 1, month: 1, year: 1, totalDays: 0,
      treasury: initialTreasury,
      reputation: 50,
      notoriety: 10,
      phase: 1,
      vehicles: [ownedVehicle],
      drivers: [],
      activeContracts: [],
      availableContracts: initialContracts,
      // 30/05/2026 — players can hide contracts they never want to see
      hiddenContractIds: [],
      credits: [initialLoan],
      // 10/02/2026 (Lot J) — Initialise la relation BNB Mariba avec le
      // crédit fondateur (loyaltyLevel = 1, déjà client à la création).
      bankRelations: {
        bnb_mariba: {
          loansRepaid: 0,
          totalBorrowed: diff.loanAmount,
          activeLoanCount: 1,
          firstLoanDay: 0,
          loyaltyLevel: 1,
        },
        societe_generaliste: { loansRepaid: 0, totalBorrowed: 0, activeLoanCount: 0, firstLoanDay: null, loyaltyLevel: 0 },
        credit_agricool: { loansRepaid: 0, totalBorrowed: 0, activeLoanCount: 0, firstLoanDay: null, loyaltyLevel: 0 },
      },
      activeInvestments: {},
      // 28/05/2026 v2 — Phase B : extended depreciation needs to know
      // when each CAPEX was activated.
      investmentPurchaseDays: {},
      // 29/05/2026 — lifetime CAPEX cumulative for regional aids
      lifetimeCapexSpent: 0,
      // 29/05/2026 — regional aids state
      activeAids: [],
      claimedOneShotAids: {},
      yearStartSnapshot: null,
      // 29/05/2026 — achievements
      unlockedAchievements: {},
      monthlyRevenues: 0,
      monthlyExpenses: 0,
      lastDayRevenue: 0,
      lastDayExpenses: 0,
      pendingEvents: [],
      eventLog: [],
      postponedEvents: [],
      // Office reset (28/05/2026 v2 — office is unlocked at 3+ support)
      officeUnlockedAt: null,
      officeUpgrades: {},
      nextOfficeProposalDay: null,
      // Reset contextual tuto state so each new game starts fresh
      contextualTutorialsSeen: {},
      activeContextualTuto: null,
      treasuryHistory: [{ day: 1, month: 1, treasury: initialTreasury, revenue: 0, expenses: 0 }],
      repHistory: [{ day: 0, month: 1, year: 1, reputation: 50, notoriety: 0 }],
      alerts: [],
  // 08/02/2026 — Q2 user prod : popups dédiés sur événements business
  // clés (dépôt surexploité, mauvaise saison, etc.). Une queue FIFO,
  // consommée par <BusinessAlertModal />. Le shape :
  //   { id, severity: 'info'|'warn'|'danger', title, body, kind, raisedOn }
  // `kind` permet d'éviter les doublons (le même type d'alerte ne se
  // déclenche pas plus d'une fois par fenêtre — voir pushBusinessAlert).
  businessAlerts: [],
  pushBusinessAlert: (alert) => {
    const state = get();
    if (!alert) return null;
    // Anti-spam : si une alerte de même kind a été émise dans les
    // 14 derniers jours, on la skip silencieusement.
    const today = state.totalDays || 0;
    const recent = (state.businessAlerts || []).some(a =>
      a.kind === alert.kind && (today - (a.raisedOn || 0)) < 14
    );
    if (recent) return null;
    const finalAlert = {
      id: `ba-${alert.kind || 'alert'}-${today}-${Math.floor(Math.random() * 1000)}`,
      severity: alert.severity || 'info',
      title: alert.title || 'Alerte',
      body: alert.body || '',
      kind: alert.kind || 'generic',
      raisedOn: today,
      cta: alert.cta || null,
    };
    set({ businessAlerts: [...(state.businessAlerts || []), finalAlert].slice(-10) });
    return finalAlert;
  },
  dismissBusinessAlert: (id) => {
    const state = get();
    set({ businessAlerts: (state.businessAlerts || []).filter(a => a.id !== id) });
  },
      selectedAdvisor: advisor,
      advisorEnabled: advisor ? true : false,
      monthlyHistory: [],
      yearlyReports: [],
      pendingAnnualReport: null,
      activeResolution: null,
      pendingAudit: null,
      auditHistory: [],
      auditsThisYear: 0,
      lastAuditYear: 0,
      attritionHistory: [],
      firedDriversArchive: {},
      // 01/06/2026 — Annual EcoVadis-style audit history (Claire Maréchal perk).
      ecoVadisHistory: [],
      // 31/05/2026 — breakdown counter for the support yearly digest
      // (Bruno Lavergne). Resets at year-end (handled in nextDay).
      breakdownsThisYear: 0,
      // 17/06/2026 (Lot 4) — annual counters for enriched support digests :
      //   • transfersInitiatedThisYear (Rémi Forni)
      //   • holidaysGrantedThisYear : total jours-congés pris (Nadia)
      //   • holidayDriversWentOnLeaveThisYear : nombre de chauffeurs ayant
      //     pris au moins un bloc de 5 j de congés
      //   • vehiclesScrappedThisYear : véhicules réformés (Bruno)
      //   • fuelTheftsResolvedThisYear : arcs vol-de-carburant résolus
      //     par fire-cause ou mutual-end (Nadia/Cyril Vasseur)
      // Toutes ces metrics sont reset à chaque clôture annuelle (cf nextDay).
      transfersInitiatedThisYear: 0,
      holidaysGrantedThisYear: 0,
      holidayDriversWentOnLeaveThisYear: 0,
      vehiclesScrappedThisYear: 0,
      fuelTheftsResolvedThisYear: 0,
      // 17/06/2026 (Lot 6 — Phase 1 Diplomatie) : initialise les relations
      // avec les concurrents par défaut à 50 (neutre). Étendu par les
      // phases suivantes (drift mensuel, OPA, sous-traitance...).
      competitorRelations: defaultRelationsFor(COMPETITORS),
      driverNegotiations: {},
      manufacturerRelations: {},
      pendingMfgTradeShow: null,
      // 11/06/2026 (soir 9) — Per-client argumentation memory. Once the
      // player picks an argument for a given client (e.g. "prix" for
      // Carrefour), subsequent contracts from that client auto-apply the
      // same choice (modal is skipped). Stored as
      //   { [clientId]: { choice: 'prix'|'délais'|'qualité'|'proximité' } }
      // The actual multiplier is recomputed at each acceptance via
      // argumentationOutcome() — driver competence and tier still matter.
      // Player can reset via the "Changer l'argumentation" link on the
      // available-contracts card.
      clientArgumentationDefaults: {},
      inbox: [],
      lastRandomMessageDay: 0,
      pendingMonthlyReport: null,
      pendingPhaseUp: null,
      tradeShowMonth: 0,
      competitorContracts: [],
      competitorEvents: [],
      challenges: [],
      challengeHistory: [],
      completedChallenges: [],
      achievements: [],
      clientRoster: null,
      pendingCompetitionEvent: null,
      pedagogicalObjectives: [],
      pendingFiscalQuiz: null,
      // Reset bankruptcy tracking on new game.
      insolvencyStreak: 0,
      bankruptcyAlertStage: null,
      bankruptcyAlertLastPushDay: 0,
      emergencyShareholderUsed: false,
      pendingShareholderMeeting: null,
      personalCapitalInjected: 0,
      fuelPriceModifier: null,
      peageStrike: null,
      gameOverState: null,
      // Reset acquisition / multi-depot state.
      pendingAcquisition: null,
      acquiredCompetitorIds: [],
      // 12/02/2026 — Reset des seuils narratifs marketScale à new game.
      marketScaleFiredThresholds: [],
      competitorAggressionUntilDay: 0,
      lastAcquisitionSummary: null,
      loiRefusalCooldown: {},
      // Reset strike modifier (national/blocking events).
      companyStrike: null,
      marketBoom: null,
      // 08/02/2026 — Reset contract history ledger on new game.
      contractLedger: [],
    });

    // 08/02/2026 — Log initial contracts as proposed (day 0).
    if (typeof get().logContractsProposed === 'function') {
      try { get().logContractsProposed(initialContracts, 0); }
      catch (err) { console.warn('[contract-history] initial log failed:', err); }
    }
    // Bootstrap the HQ depot using the chosen city.
    if (typeof get().bootstrapDepots === 'function') {
      get().bootstrapDepots(city);
    }
    // (clientLoyalty bootstrap already done earlier — before initialContracts generation)
    // Push the welcome message via the message slice (fire-and-forget).
    if (typeof get().pushMessageFromTemplate === 'function') {
      get().pushMessageFromTemplate('sys-welcome-01', {
        companyName: name, playerName: name,
      });
    }
    // 10/02/2026 (Lot J) — Welcome message Clara Fontana (BNB Mariba) :
    // présente la relation initiale (crédit fondateur) + le découvert
    // autorisé dynamique. Mémo posé après le sys-welcome.
    if (typeof get().pushCustomMessage === 'function') {
      try {
        // Compute the day-0 overdraft (creditScore null at this point →
        // grade defaults to 'C' / score = 1500 €).
        const day0State = {
          treasury: 0,
          bankRelations: get().bankRelations,
          creditScore: null,
          monthlyRevenues: 0,
        };
        const { computeOverdraftLimit } = require('../helpers/overdraftHelper');
        const { limit } = computeOverdraftLimit(day0State);
        get().pushCustomMessage({
          senderType: 'bank',
          senderName: 'Clara Fontana (BNB Mariba)',
          tone: 'friendly',
          kind: 'info',
          subject: 'Bienvenue chez BNB Mariba — votre relation est ouverte',
          body:
            `Bonjour ${name},\n\nFélicitations pour le lancement de votre société ! Nous sommes ravis de vous compter parmi nos clients.\n\n` +
            `📋 **Votre situation chez BNB Mariba** :\n` +
            `• Compte courant ouvert au nom de **${name}**\n` +
            `• Crédit fondateur de ${(diff.loanAmount || 0).toLocaleString('fr-FR')} € en cours (36 mois à 4,5 %)\n` +
            `• Relation : **Niveau 1 — Client privilégié**\n` +
            `• Découvert autorisé : **${limit.toLocaleString('fr-FR')} €**\n\n` +
            `💡 **À propos du découvert** : votre plafond évolue en fonction de votre score crédit et de notre relation. ` +
            `Plus vous remboursez ponctuellement et plus votre activité se solidifie, plus le plafond grimpe — jusqu'à 25 000 € en relation Or. ` +
            `Au-delà du plafond, le taux d'agios double (dépassement non autorisé).\n\n` +
            `À très vite,\nClara Fontana\nDirectrice d'agence PME — BNB Mariba`,
          ttlDays: 30,
        });
      } catch (err) { console.warn('[coreSlice] Clara welcome push failed:', err); }
    }
    // === "Sagesse de l'échec" : message Maurice on a fresh restart ===
    // If the player just restarted after a liquidation, deliver Maurice's
    // post-mortem advice. Consume the wisdomTips state so it doesn't fire
    // again on subsequent createCompany calls.
    const wisdom = get().wisdomTipsForNextGame;
    if (Array.isArray(wisdom) && wisdom.length > 0 && typeof get().pushCustomMessage === 'function') {
      const body = [
        `J'ai relu attentivement votre précédente tentative. Trois choses m'ont marqué — apprenons-en :`,
        ...wisdom.map((w, i) => `\n${i + 1}. ${w.cause}\n   → ${w.tip}`),
        `\nVous commencez cette fois-ci avec +5 points de réputation initiale, en reconnaissance de votre persévérance. Allez, à vous de jouer.`,
      ].join('\n');
      get().pushCustomMessage({
        senderType: 'advisor',
        senderName: 'Maurice Cassidy',
        tone: 'friendly',
        kind: 'info',
        subject: 'Sagesse de l\'échec — leçons à retenir',
        body,
        ttlDays: 60,
      });
      // Apply the +5 reputation kickstart.
      set({
        reputation: Math.min(100, (get().reputation || 50) + 5),
        wisdomTipsForNextGame: null,
      });
    }
  },

  // ─────────────────────────────────────────────────────────────────────
  // RELATIONS CONCURRENTIELLES — Phase 2 (modificateurs)
  // 17/06/2026 — Toutes les actions qui altèrent une relation passent par
  // adjustCompetitorRelation. Le drift mensuel est appelé depuis nextDay
  // (au 1er du mois). Lecture/UI : utils/competitorRelations.js.
  // ─────────────────────────────────────────────────────────────────────

  /**
   * Modifie le score de relation d'UN concurrent.
   * @param {string} competitorId
   * @param {number} delta — entier signé (cf. RELATION_DELTAS).
   * @param {string} [reason] — clé contextuelle (logguée en console).
   * @returns {{ ok: boolean, prev: number, next: number }}
   */
  adjustCompetitorRelation: (competitorId, delta, reason = 'manual') => {
    if (!competitorId || !Number.isFinite(delta) || delta === 0) {
      return { ok: false, prev: 50, next: 50 };
    }
    const state = get();
    const relations = state.competitorRelations || {};
    const prev = clampRelationScore(relations[competitorId] ?? 50);
    const next = clampRelationScore(prev + delta);
    if (prev === next) return { ok: true, prev, next };
    set({
      competitorRelations: {
        ...relations,
        [competitorId]: next,
      },
    });
    if (process.env.NODE_ENV !== 'production') {
      // Log pédagogique pour traçabilité — pas en prod (verbosité).
      // eslint-disable-next-line no-console
      console.debug(`[relations] ${competitorId} ${prev} → ${next} (${delta > 0 ? '+' : ''}${delta} · ${reason})`);
    }
    return { ok: true, prev, next };
  },

  /**
   * Applique un même delta à TOUS les concurrents sauf un (ex: M&A clan
   * effect — quand le joueur rachète un concurrent, les autres se
   * méfient et leur relation baisse).
   * @param {number} delta
   * @param {string} [excludeId] — id du concurrent exclu (le rachété).
   * @param {string} [reason]
   */
  bulkAdjustCompetitorRelations: (delta, excludeId = null, reason = 'bulk') => {
    if (!Number.isFinite(delta) || delta === 0) return { ok: false, changed: 0 };
    const state = get();
    const relations = state.competitorRelations || {};
    const ids = Object.keys(relations).filter(id => id !== excludeId);
    if (ids.length === 0) return { ok: false, changed: 0 };
    const next = { ...relations };
    ids.forEach((id) => {
      const prev = clampRelationScore(next[id] ?? 50);
      next[id] = clampRelationScore(prev + delta);
    });
    set({ competitorRelations: next });
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.debug(`[relations] bulk ${delta > 0 ? '+' : ''}${delta} on ${ids.length} competitors (${reason})`);
    }
    return { ok: true, changed: ids.length };
  },

  /**
   * Drift mensuel : ramène chaque score vers 50 d'un pas configurable
   * (par défaut 2). Les extrêmes (0/100) restent figés tant qu'une action
   * concrète ne les bouge pas (ennemi juré / allié indéfectible).
   *
   * Appelé depuis nextDay au 1er du mois (day === 1 && working).
   */
  driftCompetitorRelations: () => {
    const state = get();
    const relations = state.competitorRelations || {};
    const next = driftRelationsTowardNeutral(relations);
    set({ competitorRelations: next });
    return { ok: true, drifted: Object.keys(next).length };
  },

  nextDay: () => {
    {
      // 08/02/2026 — Bug fix critique production : seuls les events
      // RÉSOLVABLES (sans `scheduledFor` ou avec `scheduledFor` échu)
      // bloquent l'avancée du jour. Sinon, un audit/follow-up planifié
      // 60 j plus tard verrouillait le bouton à vie alors qu'aucun popup
      // n'était affichable (cf. EventPopup.jsx ligne 37, même filtre).
      const _s0 = get();
      const _td0 = _s0.totalDays || 0;
      const hasResolvable = (_s0.pendingEvents || []).some(
        e => !e.scheduledFor || e.scheduledFor <= _td0
      );
      if (hasResolvable) return false;
    }
    // 24/07/2026 — garde-fou anti-disparition (retour user prod récurrent) :
    // restaure tout chauffeur sorti de l'effectif sans départ enregistré
    // (cf. helpers/rosterGuardTick.js) AVANT de snapshotter la journée.
    runRosterGuardTick(get, set);
    const state = get();
    const totalDays = state.totalDays || 0;
    const diff = getDiff(state);

    const working = isWorkingDay(state.day);
    let dayExpenses = 0;
    const newAlerts = [];

    // 1. Calculate revenues from active contracts (extracted helper)
    const supportRevenueBonus = (typeof get().getSupportRevenueMultiplier === 'function')
      ? get().getSupportRevenueMultiplier() : 0;
    const { dayRevenue: dayRevenueRaw, contractMisses: contractMissesThisDay, marginPressure } =
      computeDailyRevenue(state, working, { supportRevenueBonus });
    let dayRevenue = dayRevenueRaw;

    // 2. Calculate daily charges (extracted helper) — also computes the
    // monthly breakdown values needed downstream for `expenseBreakdown`
    // (TVA tracking) and monthly P&L reports.
    const supportMaintenanceDiscount = (typeof get().getSupportMaintenanceDiscount === 'function')
      ? get().getSupportMaintenanceDiscount() : 0;
    const supportFuelDiscount = (typeof get().getSupportFuelDiscount === 'function')
      ? get().getSupportFuelDiscount() : 0;
    const supportMonthlyCost = (typeof get().getSupportMonthlyCost === 'function')
      ? get().getSupportMonthlyCost() : 0;
    const officeMonthlyCost = (typeof get().getOfficeMonthlyCost === 'function')
      ? get().getOfficeMonthlyCost() : 0;
    const monthlyLeaseRent = (typeof get().computeMonthlyRent === 'function')
      ? get().computeMonthlyRent().net : 0;

    const _expensesOut = computeDailyExpenses(state, working, {
      supportMaintenanceDiscount,
      supportFuelDiscount,
      supportMonthlyCost,
      officeMonthlyCost,
      monthlyLeaseRent,
    });
    dayExpenses = _expensesOut.dayExpenses;
    const monthlyRH = _expensesOut.monthlyRH;
    const monthlyMaintenance = _expensesOut.monthlyMaintenance;
    const monthlyInsurance = _expensesOut.monthlyInsurance;
    const monthlyInvestments = _expensesOut.monthlyInvestments;
    const monthlyLeasing = _expensesOut.monthlyLeasing;

    dayRevenue = Math.round(dayRevenue * diff.revenueMultiplier);
    dayExpenses = Math.round(dayExpenses * diff.expenseMultiplier);

    // 2a-bis. Strike modifier (extracted helper) — national strike or
    // blocage routier. Applied AFTER difficulty multiplier so the cut
    // reflects the actual day revenue the player sees. Auto-expires when
    // totalDays + 1 > companyStrike.until (cleanup happens elsewhere).
    const { dayRevenue: dayRevenueAfterStrike, strikeNotice } = applyStrikeModifier(state, dayRevenue);
    dayRevenue = dayRevenueAfterStrike;

    // 2b. Accounting cost for régime réel normal
    const currentRegime = TAX_REGIMES[state.taxRegime || 'reel_simplifie'];
    if (currentRegime?.accountingCost) {
      dayExpenses += Math.round(currentRegime.accountingCost / 22);
    }

    // 2b2. Export contract abroad costs (hotel, repas, divertissement, péages, assurance)
    if (working) {
      const exportContracts = (state.activeContracts || []).filter(c => c.category === 'Export EU' || c.isExport);
      if (exportContracts.length > 0) {
        const abroadCostPerDay = getAbroadDailyCost();
        // One driver abroad per export contract
        dayExpenses += abroadCostPerDay * exportContracts.length;
      }
    }

    // 2b3. Interim driver cost — 11/06/2026 (soir 9). When a titular driver
    // is on holiday and the player has enabled `useInterim` on the
    // assigned vehicle, the contract keeps running but a flat per-day
    // interim cost is charged (T1=120 ... T5=400 €).
    if (working) {
      const interimVehicles = (state.vehicles || []).filter(v => v.useInterim === true);
      interimVehicles.forEach(v => {
        // 17/07/2026 — la sous-traitance messagerie est payée D'AVANCE
        // (days × tarif dans messageSlice) : on n'ajoute pas le coût
        // quotidien d'intérim pendant la fenêtre subcontracted.
        if (v.subcontracted === true && (v.subcontractUntil || 0) > (state.totalDays || 0)) return;
        const d = (state.drivers || []).find(x => x.id === v.assignedDriverId);
        if (d && (d.onHoliday === true || d.status === 'holiday')) {
          dayExpenses += getInterimDailyCost(v.tier);
        }
      });
    }

    // 2c. Track TVA on daily revenue/expenses
    // 02/06/2026 — defensive `|| 0` (see notes above).
    const dailyFuelTotal = working ? state.vehicles.reduce((sum, v) => {
      return (v.status === 'active' || v.assignedContractId) ? sum + (v.dailyFuel || 0) : sum;
    }, 0) : 0;
    const expenseBreakdown = {
      fuel: dailyFuelTotal,
      maintenance: monthlyMaintenance / 22,
      rent: (250 + 75) / 22, // Bureau + Dépôt monthly
      leasing: monthlyLeasing / 22,
      investments: monthlyInvestments / 22,
      insurance: monthlyInsurance / 22,
      salaries: monthlyRH / 22,
    };
    if (state.calculateDailyTax) {
      state.calculateDailyTax(dayRevenue, dayExpenses, expenseBreakdown);
    }

    // 2c-bis. Daily accrual of Livret A interest on the fiscal reserve.
    if (state.accrueFiscalReserveInterestDaily) {
      state.accrueFiscalReserveInterestDaily();
    }

    // 2d. Daily XP for active drivers
    if (state.addDailyXp) {
      state.addDailyXp(working);
    }

    // 3. Update driver fatigue and satisfaction + training completion
    // 03/06/2026 — Extracted to pure helper `runDriverFatigueTick`.
    const newNotifications = [];
    if (strikeNotice) newNotifications.push(strikeNotice);
    const _fatigueMultiplier = getDriverFatigueMultiplier(state.ownedDepots || []);
    const _officeSatBoost = (typeof get().getOfficeSatisfactionBoost === 'function')
      ? get().getOfficeSatisfactionBoost() : 0;
    const _fatigueOut = runDriverFatigueTick(state, working, {
      fatigueMultiplier: _fatigueMultiplier,
      officeSatisfactionBoost: _officeSatBoost,
    });
    const updatedDrivers = _fatigueOut.updatedDrivers;
    newNotifications.push(..._fatigueOut.notifications);
    newAlerts.push(..._fatigueOut.alerts);

    // 11/06/2026 (soir 9) — Holiday cycle. 10/07/2026 (P2 dette technique) :
    // bloc inline (~160 lignes) extrait dans helpers/holidayCycleTick.js,
    // comportement inchangé (recharge 1er janv, retours, auto-trigger,
    // pression, alertes Nadia, rapport trimestriel).
    const _holidayCycleOut = runHolidayCycleTick({
      state, working, drivers: updatedDrivers, newNotifications, newAlerts, get, set,
    });
    const driversAfterHoliday = _holidayCycleOut.drivers;
    const lastHolidayQuarterlyReportDay = _holidayCycleOut.lastHolidayQuarterlyReportDay;
    const _holidaysGrantedThisTick = _holidayCycleOut.holidaysGrantedThisTick;
    const _holidayDriversStartedThisTick = _holidayCycleOut.holidayDriversStartedThisTick;
    // The orchestrator now passes `driversAfterHoliday` to subsequent
    // ticks (release-on-contract-complete, alerts, lease tick, etc.) so
    // every downstream helper sees the holiday-adjusted driver list.    // Push messagerie return-from-sick entries (side effect kept in orchestrator).
    if (_fatigueOut.sickReturnDriverIds.length > 0
        && typeof get().pushMessageFromTemplate === 'function') {
      _fatigueOut.sickReturnDriverIds.forEach(({ id, name }) => {
        get().pushMessageFromTemplate('drv-illness-return-01', { driverId: id, name });
      });
    }
    // 18/06/2026 — fin d'intérim maladie (evt-rh-02 choix 0) : le titulaire
    // reprend sa mission → on clear le flag subcontracted du véhicule.
    const _sickResumeVehicleIds = (_fatigueOut.sickReturnDriverIds || [])
      .filter(r => r.resumeVehicleId).map(r => r.resumeVehicleId);
    if (_sickResumeVehicleIds.length > 0) {
      state.vehicles = state.vehicles.map(v => _sickResumeVehicleIds.includes(v.id)
        ? {
            ...v, subcontracted: false, subcontractUntil: null,
            ...(v.interimAutoActivated ? { useInterim: false, interimAutoActivated: false } : {}),
          }
        : v);
    }
    // 04/06/2026 — Push inbox email for every training completed today.
    if ((_fatigueOut.trainingCompletedDrivers || []).length > 0
        && typeof get().pushCustomMessage === 'function') {
      _fatigueOut.trainingCompletedDrivers.forEach(({ id, name }) => {
        get().pushCustomMessage({
          senderType: 'hr',
          senderName: 'Service RH',
          tone: 'positive',
          subject: `Formation terminée — ${name}`,
          body: `${name} a achevé sa formation avec succès et reprend du service dès aujourd'hui.\n\nVous pouvez désormais l'assigner à un véhicule et à un contrat. Sa satisfaction a légèrement augmenté grâce à cette montée en compétences.`,
          kind: 'info',
          driverId: id,
          ttlDays: 14,
        });
      });
    }
    // 08/02/2026 — Q2 bug Sébastien Leclerc : reprise auto du contrat
    // après congés. On restore vehicle.assignedDriverId + status='active'
    // si le contrat lié existe toujours dans activeContracts. Si le
    // chauffeur était en sous-traitance pendant ses congés, le véhicule
    // était resté actif → on clear juste le flag subcontracted.
    if ((_fatigueOut.vacationResumeAssignments || []).length > 0) {
      const resumes = _fatigueOut.vacationResumeAssignments;
      const resumedVehicleIds = new Set();
      state.vehicles = state.vehicles.map(v => {
        const match = resumes.find(r => r.vehicleId === v.id);
        if (!match) return v;
        const stillActive = (state.activeContracts || []).find(
          c => c.driverId === match.driverId && c.vehicleId === v.id
        );
        if (!stillActive) return v;
        resumedVehicleIds.add(v.id);
        return {
          ...v,
          assignedDriverId: match.driverId,
          status: v.status === 'idle' ? 'active' : v.status,
          // Clear subcontract flags (vacation tick ended). 17/07/2026 : on
          // désarme aussi le toggle intérim s'il avait été auto-activé.
          subcontracted: false,
          subcontractUntil: null,
          ...(v.interimAutoActivated ? { useInterim: false, interimAutoActivated: false } : {}),
        };
      });
      if (resumes.length > 0 && typeof get().pushCustomMessage === 'function') {
        resumes.forEach(r => {
          const resumed = resumedVehicleIds.has(r.vehicleId);
          const subcontractNote = r.subcontract
            ? ` Pendant son absence, le contrat a été couvert par un intérimaire — aucune perte de revenu.`
            : '';
          get().pushCustomMessage({
            senderType: 'hr',
            senderName: 'Service RH',
            tone: 'positive',
            subject: resumed
              ? `Retour de congés — ${r.driverName} reprend sa mission`
              : `Retour de congés — ${r.driverName}`,
            body: resumed
              ? `${r.driverName} est de retour de congés et reprend automatiquement le contrat sur lequel il/elle était engagé(e) avant l'absence. Le véhicule repasse en mission dès aujourd'hui.${subcontractNote}`
              : `${r.driverName} est de retour de congés. Son contrat précédent n'est plus actif — vous pouvez l'assigner à une nouvelle mission.${subcontractNote}`,
            kind: 'info',
            driverId: r.driverId,
            ttlDays: 7,
          });
        });
      }
    }

    // 4. Tick contracts lifecycle (extracted helper) + dispatch CRM events.
    // 03/06/2026 — Round 3 refacto : la logique a été déportée dans
    // `tickContractsLifecycle.js` (helper pur). Le orchestrateur reste en
    // charge de dispatcher les events CRM via `applyClientEvent` (effet de
    // bord conservé ici).
    const _contractsOut = tickContractsLifecycle(state, working, contractMissesThisDay);
    let updatedContracts = _contractsOut.updatedContracts;
    // 28/09/2026 — Plafond d'équilibre de marché : marge brute réelle par
    // contrat (AO inclus) plafonnée à 65 % via renégociation progressive
    // (−3 %/jour max). cf. helpers/marketEquilibrium.js.
    const _equilibrium = applyMarketEquilibrium(state, updatedContracts, working, {
      supportRevenueBonus,
      difficultyMultiplier: diff.revenueMultiplier,
    });
    updatedContracts = _equilibrium.contracts;
    const completedContractIds = _contractsOut.completedContractIds;
    const completedPunctualIds = _contractsOut.completedPunctualIds || [];
    newNotifications.push(..._contractsOut.notifications);
    if (_contractsOut.crmEvents.length > 0 && typeof get().applyClientEvent === 'function') {
      _contractsOut.crmEvents.forEach(ev => {
        try { get().applyClientEvent(ev.clientId, ev.eventType, ev.payload); }
        catch (err) { console.warn('[crm] event dispatch failed:', err); }
      });
    }
    // 08/02/2026 — Ledger : mark completed contracts. We log here while the
    // active rows are still fresh so served/missed counters are captured.
    if (completedContractIds.length > 0 && typeof get().logContractsCompleted === 'function') {
      try { get().logContractsCompleted(completedContractIds, (state.totalDays || 0) + 1); }
      catch (err) { console.warn('[contract-history] completed log failed:', err); }
    }

    // 5. Tick vehicles (extracted helper) — wear, status flips, breakdown roll.
    const _hasDiagPrev = !!(typeof get().hasSupportUnlock === 'function' && get().hasSupportUnlock('diag_preventif'));
    const _vehiclesOut = tickVehicles(state, working, completedContractIds, {
      hasDiagPreventif: _hasDiagPrev,
      completedPunctualIds, // 04/02/2026 — Phase Q1 : 1 jour de retour pour mission ponctuelle
    });
    let updatedVehicles = _vehiclesOut.updatedVehicles;
    newAlerts.push(..._vehiclesOut.alerts);

    // 09/02/2026 — Q2 Lot I user prod : pannes véhicules — popups
    // décisionnels (Réparer / Sinistre / Hors service) + notification
    // persistante. Le bandeau alerts seul est trop discret (Dashboard
    // uniquement) ; on force une popup et on note l'inbox.
    const _breakdownPopups = _vehiclesOut.breakdownPopups || [];
    _breakdownPopups.forEach(evt => {
      newNotifications.push({
        id: generateId(),
        type: 'vehicle_breakdown',
        title: `🔧 Panne — ${evt.name.replace('Panne — ', '')}`,
        message: `Le véhicule est immobilisé. Choisissez l'action (popup décisionnelle ouverte).`,
        day: state.totalDays,
        read: false,
      });
    });

    // 09/02/2026 — Q2 Lot I user prod : véhicules rentrés de mission qui
    // avaient été ignorés par "Tout réparer". On notifie + reset le flag.
    const _pendingRepairReturns = _vehiclesOut.pendingRepairReturns || [];
    if (_pendingRepairReturns.length > 0) {
      const ids = new Set(_pendingRepairReturns.map(r => r.id));
      updatedVehicles = updatedVehicles.map(v => ids.has(v.id) ? { ...v, pendingRepair: false } : v);
      _pendingRepairReturns.forEach(({ brand, model }) => {
        newNotifications.push({
          id: generateId(),
          type: 'vehicle_repair_due',
          title: `🔧 Retour de mission — ${brand} ${model}`,
          message: `Le véhicule est rentré au dépôt. Pensez à le réparer (« Tout réparer » dans Flotte).`,
          day: state.totalDays,
          read: false,
        });
      });
    }
    // 31/05/2026 — closure-scoped breakdown counter for the support yearly
    // digest. Aggregated into state.breakdownsThisYear at the final set.
    const _breakdownsThisTick = _vehiclesOut.breakdownsThisTick;
    // 17/06/2026 — Retour auto vers le dépôt le plus proche si le dépôt
    // d'origine est saturé après une mission. Débit immédiat + transfert J+1.
    let _autoReturnCost = 0;
    if ((_vehiclesOut.autoReturns || []).length > 0) {
      const arById = new Map(_vehiclesOut.autoReturns.map(a => [a.vehicleId, a]));
      updatedVehicles = updatedVehicles.map(v => {
        const ar = arById.get(v.id);
        if (!ar) return v;
        return {
          ...v,
          status: 'transfer',
          transferArrivalDay: (state.totalDays || 0) + 1,
          transferTargetDepotId: ar.toDepotId,
        };
      });
      _vehiclesOut.autoReturns.forEach(ar => {
        _autoReturnCost += ar.cost;
        newNotifications.push({
          id: generateId(),
          type: 'auto_return_transfer',
          title: 'Retour automatique inter-dépôts',
          message: `${ar.vehicleLabel} : dépôt d'origine saturé, transfert vers le site le plus proche (convoyage −${ar.cost.toLocaleString('fr-FR')} €). Arrivée demain.`,
          day: state.totalDays,
          read: false,
        });
      });
    }
    // 04/06/2026 — Inbox + top-bar email when a vehicle arrived today.
    if ((_vehiclesOut.deliveredVehicles || []).length > 0) {
      _vehiclesOut.deliveredVehicles.forEach(({ brand, model, isLeased }) => {
        newNotifications.push({
          id: generateId(),
          type: 'vehicle_delivered',
          title: 'Véhicule livré',
          message: `${brand} ${model} est arrivé sur votre dépôt et est prêt à être assigné.`,
          day: state.totalDays + 1,
          read: false,
        });
        if (typeof get().pushCustomMessage === 'function') {
          get().pushCustomMessage({
            senderType: 'supplier',
            senderName: isLeased ? 'Concession partenaire (LOA)' : 'Concession partenaire',
            tone: 'positive',
            subject: `Votre ${brand} ${model} est arrivé`,
            body: `Bonjour,\n\nNous avons le plaisir de vous confirmer que votre ${brand} ${model} a été livré ce matin sur votre dépôt. Le véhicule est immatriculé, contrôlé et prêt à rouler.\n\nIl apparaît désormais dans votre flotte (statut « disponible »). Vous pouvez l'assigner à un chauffeur et à un contrat dans l'écran Flotte.\n\nBonnes routes !`,
            kind: 'info',
            ttlDays: 14,
          });
        }
      });
    }

    const expiredLeaseVehicles = updatedVehicles.filter(v => v.leaseExpiring);
    // 31/05/2026 — Sold vehicles (5-day resale cycle completion).
    // Filter them out of the fleet AND credit `pendingSalePrice` to
    // treasury. Drivers were already detached when sellVehicle was called.
    const soldVehicles = updatedVehicles.filter(v => v.status === 'sold');
    const soldRevenue = soldVehicles.reduce((s, v) => s + (v.pendingSalePrice || 0), 0);
    if (soldVehicles.length > 0) {
      soldVehicles.forEach(v => {
        newNotifications.push({
          id: generateId(),
          type: 'vehicle_sold',
          title: 'Véhicule vendu',
          message: `${v.brand} ${v.model} — virement reçu de ${(v.pendingSalePrice || 0).toLocaleString('fr-FR')} €.`,
          day: state.totalDays + 1,
          read: false,
        });
      });
    }
    const finalVehicles = updatedVehicles.filter(v => !v.leaseExpiring && v.status !== 'sold');
    expiredLeaseVehicles.forEach(v => {
      newNotifications.push({
        id: generateId(),
        type: 'lease_expired',
        title: 'Fin de leasing',
        message: `Le leasing du ${v.brand} ${v.model} est terminé. Le véhicule a été restitué. Vous pouvez en louer ou acheter un nouveau.`,
        day: state.totalDays + 1,
        read: false,
      });
    });
    const expiredLeaseIds = expiredLeaseVehicles.map(v => v.id);
    // 02/06/2026 — Driver release on contract completion. The pure
    // helper `releaseDriversOnContractComplete` is now extracted to
    // keep nextDay readable. Bug context kept in the helper docblock.
    // 06/02/2026 — On passe désormais completedPunctualIds + updatedVehicles
    // pour que le chauffeur revenant d'une mission ponctuelle reste en
    // statut 'returning' jusqu'à l'arrivée du camion (alignement vehicle/driver).
    const driversAfterLease = releaseDriversOnContractComplete(
      driversAfterHoliday.map(d =>
        expiredLeaseIds.includes(d.assignedVehicleId) ? { ...d, assignedVehicleId: null, status: 'idle' } : d
      ),
      completedContractIds,
      state.vehicles,
      { completedPunctualIds, updatedVehicles },
    );

    // 06/02/2026 — P1 : libération des chauffeurs en 'returning' une fois
    // leur returningUntilDay écoulé (pattern identique au camion). Sans ce
    // tick, le chauffeur resterait éternellement bloqué.
    for (let i = 0; i < driversAfterLease.length; i++) {
      const d = driversAfterLease[i];
      if (d.status === 'returning' && Number.isFinite(d.returningUntilDay)
        && (state.totalDays + 1) >= d.returningUntilDay) {
        driversAfterLease[i] = { ...d, status: 'idle', returningUntilDay: null };
      }
    }

    // 17/06/2026 — P0 healer : tickVehicles a détecté des bindings stale
    // (chauffeur en congés/arrêt/grève/formation lié à un camion non actif).
    // On finalise la réconciliation côté drivers : `assignedVehicleId` = null
    // pour ces drivers (le statut effectif est déjà géré par leur état réel
    // via driverFatigueTick / driverHolidayTick). Cf. bug user "camion +
    // chauffeur restés immobilisés sans raison".
    const _staleHealedIds = _vehiclesOut.staleDriverIds || [];
    if (_staleHealedIds.length > 0) {
      const _staleSet = new Set(_staleHealedIds);
      for (let i = 0; i < driversAfterLease.length; i++) {
        if (_staleSet.has(driversAfterLease[i].id) && driversAfterLease[i].assignedVehicleId) {
          driversAfterLease[i] = { ...driversAfterLease[i], assignedVehicleId: null };
        }
      }
    }

    // 4.5 / 4.6 — Driver alerts (burnout, low-satisfaction renegotiation)
    // 02/06/2026 — Extracted to pure helper `driverAlertsTick`. The
    // helper returns the lists of driver ids whose conditions trigger;
    // the orchestrator pushes the templated messages further down.
    const { burnoutAlerts, renegotiationAlerts } = runDriverAlertsTick(
      state,
      driversAfterLease,
      { hasSupportUnlock: get().hasSupportUnlock },
    );

    // 5. Random event — diag_preventif also halves mechanical-event probability.
    const eventProbMultiplier = diff.eventProbMultiplier *
      ((typeof get().hasSupportUnlock === 'function' && get().hasSupportUnlock('diag_preventif')) ? 0.6 : 1);
    const newEvent = generateRandomEvent(state, eventProbMultiplier);
    const newPendingEvents = newEvent ? [newEvent] : [];

    // 5.5 Tick market intel (only on working days) — refreshes competitor
    // contracts and resolves pending challenges.
    if (working && typeof get().tickMarketIntel === 'function') {
      get().tickMarketIntel();
    }

    // 6. Advance calendar
    let newDay = state.day + 1;
    let newMonth = state.month;
    let newYear = state.year;
    // 04/06/2026 hotfix — closure-scoped credits array carried to the
    // final set({}). Defaults to the unmodified array; replaced by the
    // amortization loop on month transition. Without this, the credit
    // payoff was silently dropped on rehydrate.
    let updatedCredits = state.credits || [];
    if (newDay > 30) {
      newDay = 1;
      newMonth++;
      if (newMonth > 12) {
        newMonth = 1;
        newYear++;
      }
      // 29/05/2026 — audit credit fix : immutable update + cleanup.
      // Amortissement mensuel extrait dans le helper pur
      // `runMonthlyCreditAmortization` (11/02/2026, Phase 5 refacto) :
      // pour chaque crédit actif, calcule interest + principal_portion,
      // rebuild immutable + collecte les bankId à créditer + mortgageVehicleId
      // à libérer. Voir helpers/monthlyCreditAmortization.js.
      const _amort = runMonthlyCreditAmortization(state.credits || []);
      const newCredits = _amort.updatedCredits;
      const repaidBankIds = _amort.repaidBankIds;
      const repaidMortgageVehicleIds = _amort.repaidMortgageVehicleIds;
      updatedCredits = newCredits;
      // Expose to the post-block "Release mortgages" pipeline (line ~1500).
      state._repaidMortgageVehicleIds = repaidMortgageVehicleIds;
      // Tax deadline check at month transition
      if (state.checkTaxDeadlines) {
        state.checkTaxDeadlines();
      }
      // Collect placement interest
      if (state.collectPlacementInterest) {
        state.collectPlacementInterest();
      }
      // Check for fully repaid loans → record with bank for scoring.
      // (29/05/2026 — repaidBankIds populated above during the immutable
      // credit rebuild; cleaner than re-scanning a now-cleaned array.)
      for (const bankId of repaidBankIds) {
        if (state.recordLoanRepaid) state.recordLoanRepaid(bankId);
      }
      // Credit score: check reveal at month 3, then update monthly
      if (state.checkScoreReveal) state.checkScoreReveal();
      if (state.scoreRevealed && state.updateCreditScore) state.updateCreditScore();

      // Monthly passive bonuses from hired support roles.
      // Stored on local variables to be applied within the final `set` below.
      // (Re-read just before `set` to ensure we have the up-to-date hired map.)
    }

    const _supportRepGain = (newDay === 1 && typeof get().getSupportReputationPassive === 'function')
      ? get().getSupportReputationPassive() : 0;
    const _supportSatGain = (newDay === 1 && typeof get().getSupportSatisfactionPassive === 'function')
      ? get().getSupportSatisfactionPassive() : 0;
    // Notoriety on monthly tick: support gains atténués par la courbe de
    // rendements décroissants + érosion mensuelle croissante avec le niveau
    // (22/07/2026 — le marché oublie, l'image s'entretient).
    const _supportNotoGain = (newDay === 1 && typeof get().getSupportNotorietyPassive === 'function')
      ? get().getSupportNotorietyPassive() : 0;
    const _notorietyDelta = newDay === 1
      ? scaleImageGain(state.notoriety || 10, _supportNotoGain) - monthlyNotorietyErosion(state.notoriety || 10)
      : 0;

    // Reputation penalty for excessive leasing (>70% of fleet) — applied on
    // monthly tick. Extrait dans le helper `computeMonthlyLeasingRepPenalty`
    // (11/02/2026, Phase 5 v2 refacto).
    const _leasingRepPenalty = computeMonthlyLeasingRepPenalty(state, newDay);

    // 7. Refresh available contracts — progressive top-up daily (drop stale > 14j,
    //    add fresh ones to reach the auto-sized target based on fleet + rep)
    //    instead of a brutal weekly full regen which destroyed the board every
    //    Monday and felt unfair when scaling up.
    let newAvailable = topUpAvailableContracts(state, state.availableContracts || []);
    const special = generateSpecialContract(state);
    if (special && !newAvailable.some(c => c.isSpecial)) {
      newAvailable = [...newAvailable, special];
    }

    // 08/02/2026 — Ledger : track which contracts joined the board and
    // which ones expired (dropped after 14 days). Computed by comparing
    // the previous availableContracts to the new ones.
    if (typeof get().logContractsProposed === 'function') {
      const prevIds = new Set((state.availableContracts || []).map(c => c.instanceId));
      const newIds = new Set(newAvailable.map(c => c.instanceId));
      const freshlyAdded = newAvailable.filter(c => !prevIds.has(c.instanceId));
      const droppedIds = [...prevIds].filter(id => !newIds.has(id));
      if (freshlyAdded.length > 0) {
        try { get().logContractsProposed(freshlyAdded, (state.totalDays || 0) + 1); }
        catch (err) { console.warn('[contract-history] proposed log failed:', err); }
      }
      if (droppedIds.length > 0) {
        try { get().logContractsExpired(droppedIds, (state.totalDays || 0) + 1); }
        catch (err) { console.warn('[contract-history] expired log failed:', err); }
      }
    }

    // 23/07/2026 (choix user 3a) — l'indemnité « pression congés » n'est
    // plus débitée au 1er janvier ici : elle l'est à l'expiration du
    // préavis (driverDepartureTick) ou au choix « acter le départ ».

    // 17/06/2026 — Frais de convoyage des retours automatiques inter-dépôts
    // (dépôt d'origine saturé en fin de mission).
    if (_autoReturnCost > 0) {
      dayExpenses += _autoReturnCost;
    }

    const newTreasury = state.treasury + dayRevenue - dayExpenses;

    // Agios on negative treasury (daily overdraft fee), reduced by support roles.
    // 10/02/2026 (Lot J) — Refonte : le découvert est désormais dynamique
    // (cf. `overdraftHelper`). Plafond modulé par relation BNB Mariba +
    // creditScore + CA mensuel. Au-delà du plafond, taux d'agios doublé.
    let agiosFee = 0;
    if (newTreasury < 0) {
      const _agiosReduction = (typeof get().getSupportAgiosReduction === 'function')
        ? get().getSupportAgiosReduction() : 0;
      // Calcule sur un state temporaire reflétant la nouvelle trésorerie
      // (avant agios) pour que le helper voie le bon montant utilisé.
      const tempState = { ...state, treasury: newTreasury };
      const { agios, overLimit } = computeDailyAgios(tempState);
      const effective = Math.round(agios * (1 - Math.min(_agiosReduction, 0.5)));
      agiosFee = Math.max(5, effective); // Minimum 5€/day
      newAlerts.push({
        type: 'agios',
        message: overLimit > 0
          ? `Agios : ${agiosFee} € (dépassement de découvert : -${Math.round(overLimit).toLocaleString('fr-FR')} €, taux doublé)`
          : `Agios : ${agiosFee} € (découvert utilisé : ${Math.round(newTreasury).toLocaleString('fr-FR')} €)`,
        severity: overLimit > 0 ? 'critique' : 'grave',
      });
    }
    // 31/05/2026 — Credit `soldRevenue` (5-day resale cycle completion).
    let finalTreasury = newTreasury - agiosFee + (soldRevenue || 0);

    if (finalTreasury < 500 && finalTreasury >= 0) {
      newAlerts.push({ type: 'treasury', message: `Trésorerie critique : ${Math.round(finalTreasury)} €`, severity: 'critique' });
    }

    const isNewMonth = newDay === 1;
    const updatedMonthlyRevenues = isNewMonth ? dayRevenue : state.monthlyRevenues + dayRevenue;
    const updatedMonthlyExpenses = isNewMonth ? dayExpenses : state.monthlyExpenses + dayExpenses;

    let newPhase = state.phase;
    // 08/02/2026 — Refonte phases (Q2). Plus de progression automatique
    // basée sur le temps/CA/flotte : les phases sont désormais déclenchées
    // par des ACTIONS du joueur, gérées dans les slices concernés via
    // `helpers/phaseProgression.tryPromotePhase` :
    //
    //   - P1 → P2 : achat de l'investissement « Agrandissement dépôt (x2) »
    //               (8 k€) — financeSlice.activateInvestment.
    //   - P2 → P3 : ouverture d'un dépôt FR supplémentaire OU rachat de
    //               concurrent OU achat de « Extension logistique (+100 km) »
    //               (radius +100 km) — acquisitionSlice / financeSlice.
    //   - P3 → P4 : ouverture d'un dépôt à l'étranger OU achat d'un véhicule
    //               Tier 5 — acquisitionSlice / fleetSlice.
    //
    // Le bloc historique « >= 30k €/mois + 60 j + 3 véh » a été retiré
    // (cf. handoff Q2 phases user prod). On conserve newPhase=state.phase
    // pour les downstream qui en dépendent (achievements, history snapshot).

    const newHistory = [...state.treasuryHistory, {
      day: newDay, month: newMonth, treasury: finalTreasury, revenue: dayRevenue, expenses: dayExpenses + agiosFee,
    }].slice(-90);

    // Reputation / notoriety temporal history — sampled weekly (every 7 days
    // since game start) to keep the array small. Used by /strategie sparkline.
    let updatedRepHistory = state.repHistory || [];
    if ((state.totalDays + 1) % 7 === 0 || updatedRepHistory.length === 0) {
      updatedRepHistory = [
        ...updatedRepHistory,
        {
          day: state.totalDays + 1,
          month: newMonth,
          year: newYear,
          reputation: Math.round(state.reputation || 0),
          notoriety: Math.round(state.notoriety || 0),
        },
      ].slice(-60); // ~14 months of weekly samples
    }

    // Check achievements
    const newAchievements = { ...(state.achievements || {}) };
    const achievementNotifs = [];
    const ACHIEVEMENT_DEFS = buildAchievementDefs({
      state, updatedContracts, finalVehicles, driversAfterLease, finalTreasury, newPhase, dayRevenue, dayExpenses,
    });

    ACHIEVEMENT_DEFS.forEach(a => {
      if (!newAchievements[a.id] && a.condition()) {
        newAchievements[a.id] = { unlockedAt: state.totalDays + 1 };
        achievementNotifs.push({
          id: generateId(),
          type: 'achievement',
          title: `Succès : ${a.title}`,
          message: a.message,
          day: state.totalDays + 1,
          read: false,
        });
      }
    });

    // Monthly report
    const monthlyReport = buildMonthlyReport({
      state, driversAfterLease, finalVehicles, finalTreasury, newMonth, newYear,
      // 22/07/2026 — détail des charges par poste (retour user, historique
      // annuel Finance). Snapshot des coûts mensuels au moment de la clôture.
      expenseSnapshot: {
        salaires: monthlyRH,
        support: supportMonthlyCost,
        managers: _expensesOut.monthlyManagers,
        entretien: monthlyMaintenance,
        assurances: monthlyInsurance,
        leasing: monthlyLeasing,
        credits: _expensesOut.monthlyCredit,
        investissements: monthlyInvestments,
        fixed: _expensesOut.monthlyFixed,
        depots: _expensesOut.monthlyDepots,
        bureau: officeMonthlyCost,
        loyers: monthlyLeaseRent,
        carburant: (state.monthFuelSpent || 0) + dailyFuelTotal,
        peages: (state.monthTollSpent || 0) + (_expensesOut.dailyToll || 0),
        structure: _expensesOut.monthlyStructure,
        expenseMultiplier: diff.expenseMultiplier,
      },
    });
    // CRM — monthly reputation drift (only when monthly close happens).
    if (monthlyReport && typeof get().runMonthlyReputationDrift === 'function') {
      try { get().runMonthlyReputationDrift(); } catch (err) { console.warn('[crm] monthly drift failed:', err); }
    }

    // 30/05/2026 — Réserve fiscale 25 % : automatic transfer at month close.
    // We call the action AFTER monthly report computation so the displayed
    // numbers reflect this month's activity; the transfer affects next
    // month's `treasury` going forward.
    if (monthlyReport && typeof get().transferFiscalReserveFromMonthlyProfit === 'function') {
      try {
        get().transferFiscalReserveFromMonthlyProfit(monthlyReport.netResult);
      } catch (err) { console.warn('[fiscal] reserve transfer failed:', err); }
    }

    // 28/05/2026 v2 — daily office unlock check + opportunistic supplier
    // proposals. Both are no-ops when the bureau isn't unlocked or when
    // it's not time yet (cooldown ~30 days between two proposals).
    try {
      if (typeof get().checkOfficeUnlock === 'function') get().checkOfficeUnlock();
      if (typeof get().maybeFireOfficeProposal === 'function') get().maybeFireOfficeProposal();
      // 18/06/2026 — compte à rebours siège social (45 j après la 3e
      // fonction support) : rappels + démission à l'échéance.
      if (typeof get().checkHqDeadline === 'function') get().checkHqDeadline();
    } catch (err) { console.warn('[office] daily tick failed:', err); }

    // 28/05/2026 — Force-investment monthly penalty : a recurring
    // "Plainte du voisinage" event drops reputation as long as the
    // player runs > 8 vehicles from a single depot. Surfaces in the
    // messagerie / notifications so the player sees the consequences.
    let updatedReputation = state.reputation;
    if (monthlyReport && isForceInvestActive(state)) {
      updatedReputation = Math.max(0, (state.reputation || 0) - FORCE_INVEST_REPUTATION_PENALTY);
      try {
        if (typeof get().pushCustomMessage === 'function') {
          get().pushCustomMessage({
            from: 'Mairie — Service Urbanisme',
            subject: 'Plainte de voisinage récurrente',
            body: "Plusieurs riverains se plaignent du trafic intensif de poids-lourds aux abords de votre dépôt. Cette saturation nous oblige à transmettre votre dossier à la commission. Un 2ᵉ dépôt délocaliserait une partie du flux. Réputation : -3.",
            severity: 'warning',
            day: state.totalDays + 1,
          });
        }
      } catch (err) { console.warn('[depot] neighborhood msg failed:', err); }
    }
    // 28/05/2026 v2 — monthly reputation BONUS from acquired depot #1
    // (brand recognition perk : +10 rep / month).
    if (monthlyReport) {
      const repBonus = getDepotMonthlyReputationBonus(state.ownedDepots || []);
      if (repBonus > 0) {
        updatedReputation = Math.min(100, (updatedReputation ?? state.reputation ?? 0) + repBonus);
      }
    }

    // Yearly report — end of fiscal year (360 days). Aggregates the 12 monthly
    // reports of the closing year into a single "Conseil d'administration".
    let updatedMonthlyHistory = state.monthlyHistory || [];
    let updatedCompany = state.company;
    let pendingFiscalQuiz = state.pendingFiscalQuiz || null;
    if (monthlyReport) {
      updatedMonthlyHistory = [...updatedMonthlyHistory, monthlyReport].slice(-60); // keep last 5 years
      // ============= LEGAL FORM AUTO-EVOLUTION (Sujet 2) =============
      // 02/06/2026 — Extracted to pure helper `legalFormEvolutionTick`.
      // The helper returns descriptors ; this orchestrator applies them
      // (state patch, message pushes, milestone trigger).
      const lfEv = runLegalFormEvolutionTick(state, {
        updatedMonthlyHistory,
        newYear,
      });
      if (lfEv.updatedCompany) updatedCompany = lfEv.updatedCompany;
      if (lfEv.pendingFiscalQuiz && !pendingFiscalQuiz) {
        pendingFiscalQuiz = lfEv.pendingFiscalQuiz;
      }
      if (lfEv.adminMessage) {
        const pushMsg = get().pushCustomMessage;
        if (pushMsg) pushMsg(lfEv.adminMessage);
      }
      if (lfEv.approach && typeof get().triggerMilestone === 'function') {
        const fired = get().triggerMilestone(lfEv.approach.id, '/fiscalite');
        if (fired && lfEv.approachMessage) {
          const pushMsg = get().pushCustomMessage;
          if (pushMsg) pushMsg(lfEv.approachMessage);
        }
      }
      // P2 #10: notify the player when the leasing-ratio penalty kicked in
      // this month, so they know how to recover reputation (acquire trucks).
      if (_leasingRepPenalty < 0) {
        try {
          const pushMsg = get().pushCustomMessage;
          if (pushMsg) {
            const fleet = state.vehicles || [];
            const leased = fleet.filter(v => v.isLeased).length;
            const ratio = Math.round((leased / fleet.length) * 100);
            pushMsg({
              senderType: 'media',
              senderName: 'Transports Magazine',
              tone: 'urgent',
              kind: 'warning',
              subject: 'Votre flotte est jugée "peu solide"',
              body: `Vos clients et la presse spécialisée notent un ratio de ${ratio}% de véhicules en leasing dans votre flotte. Au-delà de 70%, l'image de votre entreprise se dégrade (perçue comme moins pérenne). Réputation : ${_leasingRepPenalty} points ce mois-ci. Conseil : achetez ou financez par crédit vos prochains véhicules pour consolider votre patrimoine.`,
              ttlDays: 21,
            });
          }
        } catch (err) { console.warn('[hst-store] silent failure:', err); }
      }

      // ── DAF monthly briefing (28/05/2026 v2 — P1) ──
      // Once a DAF is hired (fiscal_simulator unlock), Tristan Midal
      // pushes a monthly Messagerie message ranking the top 3 CAPEX
      // opportunities by projected IS savings on a 24-month horizon.
      // Same source of truth as the on-screen CapexSimulator → no
      // discrepancy between the proactive nudge and the manual tool.
      try {
        const dafReport = buildDafMonthlyReport(
          { ...state, totalDays: state.totalDays + 1 },
          get().hasSupportUnlock,
        );
        if (dafReport && typeof get().pushCustomMessage === 'function') {
          get().pushCustomMessage(dafReport);
        }
      } catch (err) {
        console.warn('[coreSlice] DAF monthly report failed:', err);
      }

      // ── Achievements daily evaluation (29/05/2026) ──
      // We run on the monthly tick (cheap : ~20 boolean checks) — checking
      // every single day would be wasteful given milestones rarely flip
      // mid-month. Newly-unlocked medals trigger a Messagerie message.
      try {
        if (typeof get().evaluateAchievements === 'function') {
          get().evaluateAchievements();
        }
      } catch (err) {
        console.warn('[coreSlice] achievement evaluation failed:', err);
      }
    }
    // Trade show — annual manufacturer event (same 360-day cadence as the
    // fiscal year close). Triggered AFTER the annual report so the player
    // gets the strategic context first, then sees the show. Generates an
    // offer per manufacturer where they hold a relation (units ≥ 1).
    let pendingMfgTradeShow = state.pendingMfgTradeShow;
    if ((state.totalDays + 1) % 360 === 0 && state.totalDays > 1) {
      const generated = generateTradeShowOffers({
        ...state,
        totalDays: state.totalDays + 1,
        year: newYear,
      });
      if (generated) {
        pendingMfgTradeShow = generated;
      }
    }
    let updatedYearlyReports = state.yearlyReports || [];
    // 22/07/2026 — Activité chauffeurs (fix user « 0 trajets ») : +1 trajet
    // + CA attribué par jour ouvré servi, +1 jour manqué sinon.
    if (working) {
      applyDriverDailyActivity(driversAfterLease, state.activeContracts, contractMissesThisDay);
    }
    const annualReport = buildAnnualReport({
      state, monthlyReport, updatedMonthlyHistory, newYear, driversAfterLease, finalVehicles, finalTreasury,
    });
    // 22/07/2026 — Rapport Flotte trimestriel de Rémi Forni (gated : Forni
    // recruté) + remise à zéro des accumulateurs de période. Le trimestre
    // de clôture annuelle est couvert par le bloc Flotte du Conseil.
    {
      const _dayIdx = state.totalDays + 1;
      const _isYearClose = _dayIdx % 360 === 0 && state.totalDays > 1;
      const _isQuarterClose = _dayIdx % 90 === 0 && state.totalDays > 1;
      if (_isQuarterClose && !_isYearClose && state.hiredSupport?.['role-christophe-faure']) {
        try {
          const fleetMsg = buildFleetQuarterMessage(finalVehicles, state.activeContracts, {
            quarter: Math.round((_dayIdx % 360) / 90),
          });
          if (fleetMsg && typeof get().pushCustomMessage === 'function') {
            get().pushCustomMessage(fleetMsg);
          }
        } catch (err) {
          console.warn('[coreSlice] fleet quarter report failed:', err);
        }
      }
      if (_isQuarterClose) {
        for (let i = 0; i < finalVehicles.length; i++) {
          finalVehicles[i] = { ...finalVehicles[i], qActiveDays: 0, qDownDays: 0, qRevenue: 0, qFuelCost: 0, qFixedCost: 0 };
        }
      }
      if (_isYearClose) {
        for (let i = 0; i < finalVehicles.length; i++) {
          finalVehicles[i] = { ...finalVehicles[i], yActiveDays: 0, yDownDays: 0, yRevenue: 0, yFuelCost: 0, yFixedCost: 0 };
        }
        for (let i = 0; i < driversAfterLease.length; i++) {
          driversAfterLease[i] = { ...driversAfterLease[i], yearTrips: 0, yearRevenue: 0, yearMissedDays: 0 };
        }
      }
    }
    if (annualReport) {
      updatedYearlyReports = [...updatedYearlyReports, annualReport].slice(-20);
      // CRM — yearly "1 an sans incident" bonus pass.
      if (typeof get().runYearlyClientLoyaltyTick === 'function') {
        try { get().runYearlyClientLoyaltyTick(); } catch (err) { console.warn('[crm] yearly tick failed:', err); }
      }
      // 29/05/2026 — Regional aids evaluation (Phase A + B).
      // Triggered at fiscal year close, alongside the annual report.
      // Pushes Messagerie messages for each newly granted aid + credits
      // any one-shot subsidy on the treasury.
      try {
        if (typeof get().evaluateYearStartAids === 'function') {
          const aidsResult = get().evaluateYearStartAids();
          if (aidsResult && Array.isArray(aidsResult.messages) && typeof get().pushCustomMessage === 'function') {
            for (const msg of aidsResult.messages) {
              get().pushCustomMessage(msg);
            }
          }
        }
      } catch (err) {
        console.warn('[coreSlice] regional aids evaluation failed:', err);
      }

      // 31/05/2026 — Support roles yearly digest (Nathalie + 6 others).
      // Each hired support character pushes a personalised year-end message
      // grounded in actual KPIs (CA, contrats, churn, pannes, IS, réputation).
      // 02/06/2026 — Data-gathering plumbing extracted to
      // `supportYearlyDigestTick.buildSupportYearlyDigestMessages`.
      try {
        const liveState = get();
        const digestMessages = buildSupportYearlyDigestMessages(liveState, annualReport, newYear);
        if (digestMessages.length > 0 && typeof get().pushCustomMessage === 'function') {
          for (const msg of digestMessages) {
            get().pushCustomMessage(msg);
          }
          // 31/05/2026 — Trigger the festive Comité de direction modal
          // when at least one support character has a message. Consumed by
          // <BoardMeetingModal /> in App.js. The messages array is reused
          // verbatim for the slideshow.
          set({
            pendingBoardMeeting: {
              year: annualReport.year,
              messages: digestMessages,
            },
          });
        }
      } catch (err) {
        console.warn('[coreSlice] support yearly digest failed:', err);
      }

      // ── 01/06/2026 — Annual EcoVadis audit (Claire Maréchal perk) ──
      // Computed once a year, at the same year-end window as the digest.
      // Archives the snapshot in `ecoVadisHistory`, pushes a message in
      // the Inbox, and applies the small reputation bonus (+1 silver,
      // +3 gold) — purely informational otherwise.
      try {
        const result = buildAnnualEcoVadisAudit(get());
        if (result && typeof get().pushCustomMessage === 'function') {
          get().pushCustomMessage(result.message);
          const repBonus = getEcoVadisReputationBonus(result.audit.tier);
          set(s => ({
            ecoVadisHistory: [...(s.ecoVadisHistory || []), result.audit],
            reputation: Math.max(0, Math.min(100, (s.reputation || 0) + repBonus)),
          }));
        }
      } catch (err) {
        console.warn('[coreSlice] EcoVadis audit failed:', err);
      }
    }

    // ── 31/05/2026 — Mid-year strategic board (Jun 30 / M6 D30) ──
    // Triggers ONCE per fiscal year, on the last day of month 6. Builds
    // an H1 snapshot and stages a modal via `pendingMidYearReview`.
    // Purely informational — no resolution to vote, no treasury impact.
    let pendingMidYearReviewNext = state.pendingMidYearReview;
    // ── 01/06/2026 — Phase 5 DREAL fine accumulators ──
    // Populated inside the daily-events try block, applied below to
    // treasury / reputation / cooldown map. Declared at this scope so
    // they survive the try{} and reach the final set() call.
    let drealFineTotal = 0;
    let drealRepHit = 0;
    const drealCooldownUpdates = {};
    try {
      // ── 02/06/2026 — Nathalie daily messaging tick (extracted) ──
      // All scheduled Nathalie messages (board memo, weekly recap, monthly
      // advice, monthly AO recap) are now collected by a pure helper.
      const nathalieMessages = buildNathalieDailyMessages(state);
      if (nathalieMessages.length > 0 && typeof get().pushCustomMessage === 'function') {
        nathalieMessages.forEach(msg => get().pushCustomMessage(msg));
      }

      // ── Drain deferred messages (Nathalie event follow-ups, etc.) ──
      // Each entry was enqueued at the moment of a trigger event
      // (depot opening, M&A closing, senior hire). They surface in the
      // inbox AFTER N days — the day-after by default, mimicking how an
      // assistant would prepare paperwork "the next morning".
      if (typeof get().flushDeferredMessages === 'function') {
        try { get().flushDeferredMessages(); }
        catch (err) { console.warn('[coreSlice] flushDeferredMessages failed:', err); }
      }

      // 04/06/2026 — Spot exclusivity engagements daily tick: spawn calls
      // scheduled for today, expire calls past their response deadline,
      // close engagements that reached their 90-day endsOnDay.
      // 25/06/2026 (hotfix) — DÉPLACÉ APRÈS le set() final pour que ses
      // mutations de treasury/vehicles/drivers ne soient pas écrasées par
      // les variables locales (finalTreasury, _finalVehiclesPostMortgage,
      // driversAfterLease) calculées en amont. Voir bloc à la fin de
      // nextDay. Idem pour `tickCommercialOpportunities`.

      // 10/02/2026 (Lot J1) — Retrait du mail one-shot DAF « Nouvelle
      // décomposition fiscale du bilan ». Retour user prod : la phrase en
      // gras apparaissait répétitivement à l'écran (perçue comme du spam
      // sans valeur ajoutée). La correction du bilan est désormais en
      // place depuis plusieurs itérations — le mail de migration n'a
      // plus de raison d'être. Flag conservé en state pour ne pas casser
      // la persistence des anciennes parties.
      try {
        const _hasTMS = !!(state.activeInvestments || {})['inv-tech-01'];
        const _hired = state.hiredSupport || {};
        const _commercialRoleId = _hired['role-lea-bertrand']
          ? 'role-lea-bertrand'
          : _hired['role-marc-dupuis']
            ? 'role-marc-dupuis'
            : _hired['role-sylvie-marchand']
              ? 'role-sylvie-marchand'
              : null;
        const _commercialName = _commercialRoleId === 'role-lea-bertrand' ? 'Léa Bertrand'
          : _commercialRoleId === 'role-marc-dupuis' ? 'Marc Dupuis'
            : _commercialRoleId === 'role-sylvie-marchand' ? 'Sylvie Marchand'
              : null;
        if (_hasTMS && _commercialRoleId && !state.hideUnprofitableEmailSent
            && typeof get().pushCustomMessage === 'function') {
          get().pushCustomMessage({
            senderType: 'support',
            senderName: _commercialName,
            tone: 'friendly',
            kind: 'info',
            subject: 'Filtrons les contrats non rentables ?',
            body: `Bonjour,\n\nMaintenant que le TMS est en place et que je peux mesurer la marge nette de chaque contrat, je vous propose d'activer un **filtre « Masquer non rentables »** sur le tableau de bord.\n\nCela permet de ne plus afficher les contrats dont la marge estimée est négative. Vous gagnerez en temps de décision et éviterez les pertes d'opportunité sur les missions à fort potentiel.\n\nLe filtre est désactivé par défaut. Pour l'activer : cochez la case « Masquer non rentables » en haut du tableau des contrats disponibles. Vous pourrez le désactiver à tout moment.\n\nÀ votre disposition pour en parler,\n\n${_commercialName}\nFonction commerciale support`,
            ttlDays: 21,
            payload: { kind: 'hide_unprofitable_proposal' },
          });
          set({ hideUnprofitableEmailSent: true });
        }
      } catch (err) {
        console.warn('[coreSlice] hideUnprofitable email push failed:', err);
      }

      // ── 01/06/2026 — Claire HSE compliance alerts (Phase 4 FCO + Phase 5
      //    full training matrix : FCO, médical, ADR, CACES). The pure
      //    helper `runHseComplianceTick` returns the deferred memos to
      //    enqueue ; the orchestrator only handles the actual side-effect.
      //    (02/06/2026 — extracted from inline scan for testability.)
      if (typeof get().enqueueDeferredMessage === 'function') {
        const deferred = runHseComplianceTick(driversAfterLease, state.totalDays, state.hiredSupport);
        deferred.forEach(({ dueDay, memo, key }) => {
          get().enqueueDeferredMessage(dueDay, memo, key);
        });
      }

      // ── 01/06/2026 — Phase 5 gameplay penalty : DREAL roadside fine ──
      //
      // Pure helper `runDrealFineTick` rolls each (driver, training) pair
      // and returns the aggregate deltas (treasury debit, rep hit, memos,
      // notifs). Gated inside the helper on Claire being hired AND it
      // being a working day. (02/06/2026 — extracted from inline roll
      // for testability.)
      const drealResult = runDrealFineTick(state, driversAfterLease, isWorkingDay);
      drealFineTotal += drealResult.totalFine;
      drealRepHit += drealResult.repHit;
      Object.assign(drealCooldownUpdates, drealResult.cooldownUpdates);
      if (typeof get().pushCustomMessage === 'function') {
        drealResult.memos.forEach(memo => get().pushCustomMessage(memo));
      }
      achievementNotifs.push(...drealResult.notifications);

      // The state used here is the PRE-flip state (month/day not yet
      // bumped) so we detect end of month 6.
      if (state.month === 6 && state.day === 30 && !state.pendingMidYearReview) {
        const review = buildMidYearReview(
          state,
          finalTreasury,
          finalVehicles,
          driversAfterLease,
        );
        if (review) {
          pendingMidYearReviewNext = review;
        }
      }
    } catch (err) {
      console.warn('[coreSlice] mid-year review build failed:', err);
    }

    // ── 01/06/2026 — Apply DREAL fine deltas (Phase 5) ──
    // Subtract the accumulated fines from the post-tick treasury and let
    // the reputation calculation in the final set() use `drealRepHit` to
    // dock the reputation accordingly. Cooldowns are merged into state.
    if (drealFineTotal > 0) {
      finalTreasury -= drealFineTotal;
    }

    // ── 01/06/2026 — Tender / AO subsystem daily tick ──
    // 1) Resolve any pending applications whose decision day has come.
    //    This may push 'won' contracts to availableContracts, stage
    //    counter-offers, or archive losses — all via the slice's own
    //    set() calls. Done BEFORE refreshTenders so a tender that just
    //    resolved frees its board slot in the same tick.
    // 2) Refresh the AO board (drop expired, add new ones up to the cap).
    //    Gated behind an at-least-one-commercial check inside the slice.
    try {
      if (typeof get().resolvePendingApplications === 'function') {
        get().resolvePendingApplications();
      }
      if (typeof get().refreshTenders === 'function') {
        // Weekly refresh tick : only on Mondays (day % 7 === 1) to avoid
        // flooding the board with announcements.
        if ((state.day || 1) % 7 === 1) {
          get().refreshTenders();
        }
      }
      // 04/02/2026 — Q1 Bug B Messagerie : rappels + annulation auto sur
      // les AO gagnés non staffés sous 10 jours.
      if (typeof get().tickTenderStaffingDeadline === 'function') {
        get().tickTenderStaffingDeadline();
      }
    } catch (err) {
      console.warn('[coreSlice] tender tick failed:', err);
    }

    // Check active challenge progress
    let challengeUpdate = {};
    if (state.activeChallenge && !state.activeChallenge.completed && !state.activeChallenge.failed) {
      const ch = CHALLENGES.find(c => c.id === state.activeChallenge.id);
      if (ch) {
        const futureState = {
          ...state,
          totalDays: state.totalDays + 1,
          treasury: finalTreasury,
          vehicles: finalVehicles,
          drivers: driversAfterLease,
          reputation: Math.max(0, Math.min(100, state.reputation + (dayRevenue > dayExpenses ? diff.reputationGain : -diff.reputationLoss))),
          phase: newPhase,
        };
        const elapsed = (state.totalDays + 1) - state.activeChallenge.startDay;
        const allObjMet = ch.objectives.every(obj => obj.format === 'constraint' ? obj.check(futureState) : obj.check(futureState));
        const constraintFailed = ch.objectives.some(obj => obj.format === 'constraint' && !obj.check(futureState));
        const timeUp = elapsed >= ch.timeLimitDays;

        if (constraintFailed) {
          challengeUpdate = { activeChallenge: null };
          achievementNotifs.push({
            id: generateId(), type: 'challenge_failed', title: 'Challenge échoué',
            message: `"${ch.name}" — Contrainte non respectée !`, day: state.totalDays + 1, read: false,
          });
        } else if (allObjMet) {
          challengeUpdate = {
            activeChallenge: null,
            completedChallenges: { ...(state.completedChallenges || {}), [ch.id]: { completedAt: state.totalDays + 1 } },
          };
          // +8 notoriety reward (press article / publicity boost from challenge completion).
          state._challengeNotorietyBonus = (state._challengeNotorietyBonus || 0) + 8;
          achievementNotifs.push({
            id: generateId(), type: 'challenge_won', title: 'Challenge réussi !',
            message: `"${ch.name}" terminé en ${elapsed} jours ! +8 notoriété (couverture presse) — ${ch.reward}`, day: state.totalDays + 1, read: false,
          });
        } else if (timeUp) {
          challengeUpdate = { activeChallenge: null };
          achievementNotifs.push({
            id: generateId(), type: 'challenge_failed', title: 'Challenge échoué',
            message: `"${ch.name}" — Temps écoulé !`, day: state.totalDays + 1, read: false,
          });
        }
      }
    }

    // Competition events — random trigger
    const compEvent = rollCompetitionEvent({ state, driversAfterLease, dayRevenue, working });
    if (compEvent && state.triggerCompetitionEvent) {
      state.triggerCompetitionEvent(compEvent);
    }

    // Release mortgages on vehicles whose backing loan was fully repaid
    // in THIS tick. (29/05/2026 — the previous logic re-scanned
    // `state.credits` for entries with `remainingMonths <= 0`, but the
    // new immutable cleanup removes them as soon as they finish. We now
    // consume `state._repaidMortgageVehicleIds` populated by the loan
    // reconciliation block above.)
    const _fullyRepaidVehicleIds = state._repaidMortgageVehicleIds || new Set();
    let _finalVehiclesPostMortgage = _fullyRepaidVehicleIds.size > 0
      ? finalVehicles.map(v =>
          v.mortgaged && _fullyRepaidVehicleIds.has(v.id)
            ? { ...v, mortgaged: false, mortgageLoanId: null }
            : v
        )
      : finalVehicles;

    // Auto-expire trade-show DEMO vehicles : when demoExpiresOn ≤ today, the
    // truck is returned to the manufacturer. Driver freed (idle), assigned
    // contract terminated gracefully (no penalty — the player knew the
    // deadline). A messagerie notification is pushed.
    const _expiredDemoIds = [];
    _finalVehiclesPostMortgage = _finalVehiclesPostMortgage.filter(v => {
      if (v.isDemo && v.demoExpiresOn && (state.totalDays + 1) >= v.demoExpiresOn) {
        _expiredDemoIds.push(v.id);
        return false;
      }
      return true;
    });
    if (_expiredDemoIds.length > 0 && typeof get().pushMessageFromTemplate === 'function') {
      // Free assigned drivers, terminate contracts gracefully.
      const _expiredSet = new Set(_expiredDemoIds);
      const _affectedDrivers = (state.drivers || []).filter(d =>
        finalVehicles.some(v => _expiredSet.has(v.id) && v.assignedDriverId === d.id)
      );
      _affectedDrivers.forEach(d => {
        get().pushMessageFromTemplate('drv-illness-return-01', {
          driverId: d.id,
          name: d.name,
        });
      });
    }

    // ============= BANKRUPTCY / GAME OVER DETECTION =============
    // 02/06/2026 — Extracted to pure helper `bankruptcyTick`. The
    // orchestrator computes the post-tick balance sheet, calls the
    // helper, and merges the descriptors below.
    const _postTickSheet = computeBalanceSheet({
      treasury: finalTreasury,
      vehicles: _finalVehiclesPostMortgage,
      credits: updatedCredits,
      activePlacements: state.activePlacements || [],
      totalDays: state.totalDays + 1,
      tvaCollectedTotal: state.tvaCollectedTotal || 0,
      tvaDeductibleTotal: state.tvaDeductibleTotal || 0,
      annualProfit: state.annualProfit || 0,
    });
    const _bk = runBankruptcyTick(state, {
      postTickSheet: _postTickSheet,
      finalTreasury,
      newDay,
    });
    if (_bk.messages.length > 0 && typeof get().pushCustomMessage === 'function') {
      _bk.messages.forEach(m => {
        try { get().pushCustomMessage(m); }
        catch (err) { console.warn('[hst-store] silent failure:', err); }
      });
    }
    // 08/02/2026 — Refonte phases (Q2). Liquidation prononcée → vidéo
    // Sora 2 dédiée (game-over) en plus de la modale Game Over existante.
    if (_bk.gameOverState && !state.gameOverState
      && typeof get().triggerMilestoneVideo === 'function') {
      try {
        get().triggerMilestoneVideo({
          kind: 'game-over',
          reason: 'Liquidation judiciaire prononcée',
        });
      } catch (err) { console.warn('[hst-store] game-over video trigger failed:', err); }
    }

    // ============= LEGAL FORM AUTO-APPLY =============
    // 03/06/2026 round 4 — extracted to pure helper. Resolves the
    // `company.pendingLegalFormChange` against the two trigger windows
    // (manual deferred / auto Jan 1st) and returns the next `company`
    // + an optional player-facing message.
    const _lfApply = applyPendingLegalFormChange(state, updatedCompany, newDay, newMonth, newYear);
    if (_lfApply.message && typeof get().pushCustomMessage === 'function') {
      try { get().pushCustomMessage(_lfApply.message); }
      catch (err) { console.warn('[hst-store] legal form message push failed:', err); }
    }

    set({
      day: newDay,
      month: newMonth,
      year: newYear,
      totalDays: state.totalDays + 1,
      treasury: finalTreasury,
      vehicles: _finalVehiclesPostMortgage,
      // 10/07/2026 (fix user prod) — réconciliation finale : tout chauffeur
      // référencé par un contrat actif est requalifié « en mission »
      // (sauf congés/maladie couverts par l'intérim).
      drivers: reconcileDriverContractStatus(
        (_supportSatGain > 0
          ? driversAfterLease.map(d => ({ ...d, satisfaction: Math.min(100, (d.satisfaction || 0) + _supportSatGain) }))
          : driversAfterLease
        ).map(d => {
          let next = d;
          if (burnoutAlerts.includes(d.id)) next = { ...next, burnoutAlertCooldown: state.totalDays + 30 };
          if (renegotiationAlerts.includes(d.id)) next = { ...next, renegotiationCooldown: state.totalDays + 90 };
          return next;
        }),
        updatedContracts,
      ),
      activeContracts: updatedContracts,
      availableContracts: newAvailable,
      // 04/06/2026 hotfix — repaid credits must be persisted to the store.
      // Previously the amortization loop mutated state in-place and the set()
      // never carried the new array, so each month transition was discarded.
      credits: updatedCredits,
      pendingEvents: [...state.pendingEvents, ...newPendingEvents, ..._breakdownPopups],
      lastDayRevenue: dayRevenue,
      lastDayExpenses: dayExpenses,
      monthlyRevenues: updatedMonthlyRevenues,
      monthlyExpenses: updatedMonthlyExpenses,
      // 22/07/2026 — accumulateurs carburant/péages réels du mois (détail
      // des charges par poste). Reset après chaque clôture mensuelle.
      monthFuelSpent: monthlyReport ? 0 : (state.monthFuelSpent || 0) + dailyFuelTotal,
      monthTollSpent: monthlyReport ? 0 : (state.monthTollSpent || 0) + (_expensesOut.dailyToll || 0),
      // 22/07/2026 — rééquilibrage image : gains à rendements décroissants
      // (scaleImageGain) + érosion mensuelle. Cf. helpers/imageGainCurve.js.
      reputation: Math.max(0, Math.min(100, (updatedReputation ?? state.reputation)
        + scaleImageGain(updatedReputation ?? state.reputation, (dayRevenue > dayExpenses ? diff.reputationGain : 0) + _supportRepGain)
        - (dayRevenue > dayExpenses ? 0 : diff.reputationLoss)
        + _leasingRepPenalty - drealRepHit
        - (newDay === 1 ? monthlyReputationErosion(updatedReputation ?? state.reputation) : 0))),
      notoriety: Math.max(0, Math.min(100, (state.notoriety || 10) + _notorietyDelta + scaleImageGain(state.notoriety || 10, state._challengeNotorietyBonus || 0))),
      phase: newPhase,
      // 03/06/2026 — déclencher la modale PhaseNotification UNIQUEMENT
      // sur transition (newPhase > state.phase). Le composant est déjà
      // monté dans App.js mais le setter avait été perdu.
      pendingPhaseUp: newPhase > state.phase ? newPhase : (state.pendingPhaseUp || null),
      treasuryHistory: newHistory,
      // 29/09/2026 — historique quotidien de la marge brute moyenne (onglet
      // Finance › Marché : courbe vs plafond 65 %).
      marginHistory: appendMarginHistory(state.marginHistory, _equilibrium.snapshot),
      repHistory: updatedRepHistory,
      alerts: newAlerts,
      // 01/06/2026 — DREAL fine cooldown map (Phase 5). Keyed by
      // `${driverId}-${trainingKey}` → next eligible day for that fine.
      drealFineCooldowns: Object.keys(drealCooldownUpdates).length > 0
        ? { ...(state.drealFineCooldowns || {}), ...drealCooldownUpdates }
        : (state.drealFineCooldowns || {}),
      // 31/05/2026 — Bruno Lavergne yearly digest counter. Reset to 0 when
      // the annual report fires (year close); otherwise accumulate.
      breakdownsThisYear: annualReport ? 0 : ((state.breakdownsThisYear || 0) + _breakdownsThisTick),
      // 17/06/2026 (Lot 4) — annual counters for enriched support digests.
      // Reset to 0 at year-close (after the digest has consumed them).
      transfersInitiatedThisYear: annualReport ? 0 : (state.transfersInitiatedThisYear || 0),
      holidaysGrantedThisYear: annualReport ? 0 : ((state.holidaysGrantedThisYear || 0) + _holidaysGrantedThisTick),
      holidayDriversWentOnLeaveThisYear: annualReport ? 0 : ((state.holidayDriversWentOnLeaveThisYear || 0) + _holidayDriversStartedThisTick),
      vehiclesScrappedThisYear: annualReport ? 0 : (state.vehiclesScrappedThisYear || 0),
      fuelTheftsResolvedThisYear: annualReport ? 0 : (state.fuelTheftsResolvedThisYear || 0),
      // 31/05/2026 — Mid-year review staging. Stays null on most ticks,
      // populated by buildMidYearReview at M6 D30, cleared by dismiss.
      pendingMidYearReview: pendingMidYearReviewNext,
      notifications: [...(state.notifications || []).slice(-30), ...newNotifications, ...achievementNotifs],
      achievements: newAchievements,
      pendingMonthlyReport: monthlyReport || state.pendingMonthlyReport,
      pendingAnnualReport: annualReport || state.pendingAnnualReport,
      pendingMfgTradeShow,
      monthlyHistory: updatedMonthlyHistory,
      yearlyReports: updatedYearlyReports,
      pendingFiscalQuiz,
      // ============= BANKRUPTCY STATE PERSISTENCE =============
      insolvencyStreak: _bk.nextStreak,
      bankruptcyAlertStage: _bk.alertStage,
      bankruptcyAlertLastPushDay: _bk.alertLastPushDay,
      pendingShareholderMeeting: _bk.pendingShareholderMeeting,
      gameOverState: _bk.gameOverState,
      // Auto-clear expired strike modifier.
      companyStrike: (state.companyStrike && (state.totalDays + 1) > (state.companyStrike.until || 0)) ? null : state.companyStrike,
      // 08/02/2026 — Q2 Lot D : auto-clear expired market boom.
      marketBoom: (state.marketBoom && (state.totalDays + 1) > (state.marketBoom.endDay || 0)) ? null : state.marketBoom,
      // 17/06/2026 Lot 4 — stamp the day of the last holiday quarterly report
      // so the next one fires 90 days from now.
      lastHolidayQuarterlyReportDay,
      // ============= LEGAL FORM AUTO-APPLY =============
      // 03/06/2026 round 4 — résultat précalculé par le helper
      // `applyPendingLegalFormChange` plus haut.
      company: _lfApply.company,
      // Reset the burnout-alert counter at year close so the next year
      // starts fresh. If no annual report fired this tick, keep the current
      // counter.
      burnoutAlertsResolvedYearly: annualReport ? 0 : (state.burnoutAlertsResolvedYearly || 0),
      // 25/07/2026 — journal des rétentions actives (démissions évitées
      // hors burnout), remis à zéro au 1er janvier comme le compteur burnout.
      retentionSavesYearly: annualReport ? [] : (state.retentionSavesYearly || []),
      ...challengeUpdate,
      _challengeNotorietyBonus: 0,
    });

    // 10/07/2026 (fix user prod) — rattrapage Phase 4 : un Tier 5 acquis par
    // une voie sans promotion (leasing, occasion, bundle salon, héritage M&A)
    // débloque la phase 4 au tick suivant. Auto-guérit les parties existantes.
    try {
      const _postPhase = get();
      if ((_postPhase.phase || 1) < 4 && (_postPhase.vehicles || []).some(v => !v.isDemo && (v.tier || 1) >= 5)) {
        tryPromotePhase(get, set, 4, 'Un véhicule Tier 5 est en flotte');
      }
    } catch (err) { console.warn('[coreSlice] phase-4 catch-up failed:', err); }

    // Monthly driver attrition roll — runs AFTER the main commit so it
    // operates on the up-to-date drivers/vehicles state. Low-satisfaction
    // idle drivers may resign; `turnover_reduction` (Élise Vasseur, Karim
    // Bensalem) mitigates the chance.
    if (newDay === 1 && typeof get().rollMonthlyAttrition === 'function') {
      get().rollMonthlyAttrition();
    }

    // 25/06/2026 (hotfix bug user) — Ticks dépendant de l'état flotte/treso
    // déplacés ici APRÈS le set() final pour éviter que leurs mutations
    // soient écrasées par les variables locales (finalTreasury,
    // _finalVehiclesPostMortgage, driversAfterLease) capturées avant le tick.
    // Conséquence du bug avant le déplacement : les opportunités terminées
    // ne libéraient pas le camion/chauffeur et ne créditaient pas la
    // trésorerie. Voir messagerie user 25/06/2026 (« Lucie + L2H2 coincés »).
    if (typeof get().tickSpotEngagements === 'function') {
      try { get().tickSpotEngagements(); }
      catch (err) { console.warn('[coreSlice] tickSpotEngagements failed:', err); }
    }
    if (typeof get().tickCommercialOpportunities === 'function') {
      try { get().tickCommercialOpportunities(); }
      catch (err) { console.warn('[coreSlice] tickCommercialOpportunities failed:', err); }
    }
    // 05/02/2026 (Q1 retour user prod) — Tick convois exceptionnels :
    // résout les missions terminées (succès/aléa/échec narratifs) et
    // propose sporadiquement de nouvelles missions si permit + Heavyhaul
    // possédés.
    if (typeof get().tickConvoyExceptionnel === 'function') {
      try { get().tickConvoyExceptionnel(); }
      catch (err) { console.warn('[coreSlice] tickConvoyExceptionnel failed:', err); }
    }
    // 05/02/2026 (Q1 retour user prod) — Tick accidents du travail :
    // probabilité quotidienne par chauffeur actif (modulée par fatigue +
    // trigger événementiel route longue), + résolution des contentieux
    // prud'homaux ouverts.
    if (typeof get().tickWorkAccidents === 'function') {
      try { get().tickWorkAccidents(); }
      catch (err) { console.warn('[coreSlice] tickWorkAccidents failed:', err); }
    }
    // 05/02/2026 (Q1 Lot 2) — Tick salon marketing : invitation tous les
    // ~210 jours (~7 mois) si fonction Marketing recrutée, et résolution
    // du bilan 7 jours après inscription (notoriété + réputation + prospects).
    if (typeof get().tickMarketingSalon === 'function') {
      try { get().tickMarketingSalon(); }
      catch (err) { console.warn('[coreSlice] tickMarketingSalon failed:', err); }
    }

    // 17/06/2026 — Rémi Forni v2 : promotion automatique des contrats
    // acceptés en différé pendant qu'un camion était en transfert inter-
    // dépôts. Doit s'exécuter APRÈS le set() final pour que get().vehicles
    // reflète l'état post-tickVehicles (status === 'idle' à l'arrivée).
    if (typeof get().promotePendingTransferContracts === 'function') {
      try { get().promotePendingTransferContracts(); }
      catch (err) { console.warn('[coreSlice] promotePendingTransferContracts failed:', err); }
    }

    // 08/02/2026 — URSSAF tick quotidien (reminder J-15 + déclaration
    // J+1). DOIT s'exécuter APRÈS le set() final pour que get().day et
    // get().month reflètent la nouvelle date.
    if (typeof get().checkUrssafDeadlines === 'function') {
      try { get().checkUrssafDeadlines(); }
      catch (err) { console.warn('[coreSlice] checkUrssafDeadlines failed:', err); }
    }

    // 11/02/2026 (Phase 2 — Pédagogie apprenant) — Détection quotidienne
    // des concepts pédagogiques nouvellement rencontrés (premier contrat,
    // premier crédit, premier IS, …). Idempotent, best-effort : les
    // concepts déjà découverts sont ignorés. Toasts poussés dans la file
    // `pendingConceptToasts` consommée par ConceptUnlockedToast.jsx.
    if (typeof get().evaluatePedagogyConcepts === 'function') {
      try { get().evaluatePedagogyConcepts(); }
      catch (err) { console.warn('[coreSlice] evaluatePedagogyConcepts failed:', err); }
    }

    // 11/02/2026 (Phase 3 — Superpouvoirs formateur) — Expiration des
    // scénarios pédagogiques temporaires (fuel_shock, zfe_ban). Le
    // `companyStrike` a son propre auto-clear plus haut ; `crueEvent`
    // est one-shot.
    if (typeof get().tickScenarioExpirations === 'function') {
      try { get().tickScenarioExpirations(); }
      catch (err) { console.warn('[coreSlice] tickScenarioExpirations failed:', err); }
    }

    // 18/09/2026 (REX apprenant) — Suivi des opérations flotte : avance les
    // dossiers ventes/sinistres, crédite les indemnités échues, confirme
    // les virements. DOIT s'exécuter après le set() (totalDays à jour).
    if (typeof get().tickFleetOps === 'function') {
      try { get().tickFleetOps(); }
      catch (err) { console.warn('[coreSlice] tickFleetOps failed:', err); }
    }

    // 18/09/2026 (REX apprenant) — Impact client renforcé : pénalités
    // contractuelles + réputation + bouche-à-oreille sur livraisons manquées.
    if (typeof get().tickClientServicePenalties === 'function') {
      try { get().tickClientServicePenalties(contractMissesThisDay); }
      catch (err) { console.warn('[coreSlice] tickClientServicePenalties failed:', err); }
    }

    // 08/02/2026 — Q2 user fix : process pending departure letters
    // (push the formal "préavis" email) + auto-finalize expired notices.
    try { _processDepartureNotices(get, set); }
    catch (err) { console.warn('[coreSlice] departure notices failed:', err); }

    // 08/02/2026 — Q2 user prod : popup « Dépôt surexploité » si au
    // moins un dépôt dépasse sa capacité véhicules. Anti-spam : géré
    // par pushBusinessAlert (cooldown 14 j même `kind`).
    try {
      const cur = get();
      const ownedDepots = cur.ownedDepots || [];
      const vehicles = cur.vehicles || [];
      const overloaded = ownedDepots.filter(d => {
        const cap = d.capacityVehicles || 10;
        const occupancy = vehicles.filter(v => v.status !== 'sold' && (v.depotId || 'depot-hq') === d.id).length;
        return occupancy > cap;
      });
      if (overloaded.length > 0 && typeof get().pushBusinessAlert === 'function') {
        const depot = overloaded[0];
        const cap = depot.capacityVehicles || 10;
        const occupancy = vehicles.filter(v => v.status !== 'sold' && (v.depotId || 'depot-hq') === depot.id).length;
        get().pushBusinessAlert({
          kind: `depot-overload-${depot.id}`,
          severity: 'warn',
          title: `Dépôt surexploité — ${depot.name || (depot.isHeadquarters ? 'QG' : 'Dépôt')}`,
          body: `Votre dépôt **${depot.name || (depot.isHeadquarters ? 'QG' : 'Dépôt')}** abrite ${occupancy} véhicules pour une capacité de ${cap}. Les rotations vont être ralenties et la maintenance plus coûteuse.\n\nDeux options :\n• **Agrandir** ce dépôt depuis l'onglet Dépôt.\n• **Ouvrir un nouveau dépôt** plus proche de vos contrats actifs.`,
        });
      }
    } catch (err) { console.warn('[coreSlice] depot overload check failed:', err); }

    // 17/06/2026 (Lot 6 Phase 2) — Drift mensuel des relations
    // concurrentielles vers la neutralité (50). Déclenché au 1er du mois.
    // Les extrêmes (ennemi 0 / allié 100) sont figés tant qu'une action
    // concrète ne les bouge pas (cf. driftRelationsTowardNeutral).
    if (isNewMonth && typeof get().driftCompetitorRelations === 'function') {
      try { get().driftCompetitorRelations(); }
      catch (err) { console.warn('[coreSlice] driftCompetitorRelations failed:', err); }
    }

    // 09/02/2026 (Q2 Lot G user prod) — Récap RH mensuel envoyé dans la
    // Messagerie le 1er de chaque mois. Consolide effectif, fatigue,
    // satisfaction, préavis et vivier candidats pour éviter la noyade
    // de notifications RH individuelles.
    if (isNewMonth && typeof get().pushCustomMessage === 'function') {
      try {
        const { buildMonthlyHrRecapMessage } = require('../helpers/monthlyHrRecap');
        const recap = buildMonthlyHrRecapMessage(get());
        if (recap) get().pushCustomMessage(recap);
      } catch (err) { console.warn('[coreSlice] monthly HR recap failed:', err); }
    }

    // 12/02/2026 — Événement narratif « montée en puissance » (presse
    // spécialisée) déclenché quand `marketScaleFromState` franchit un
    // palier (1.15, 1.30, 1.45). Le message est signé « Actu Transport »
    // et rend visible le scaling dynamique concurrent + AO.
    // Rétro-effet : chaque seuil franchi active une fenêtre de 30 jours
    // où les concurrents sont plus agressifs sur les contre-offres d'AO
    // (cf. tenderSlice.resolveTenderApplications + rollTenderOutcome).
    if (isNewMonth && typeof get().pushCustomMessage === 'function') {
      try {
        const { buildMarketScaleNarrative } = require('../helpers/marketScaleNarrative');
        const narrative = buildMarketScaleNarrative(get());
        if (narrative) {
          get().pushCustomMessage(narrative.payload);
          const prev = get().marketScaleFiredThresholds || [];
          set({
            marketScaleFiredThresholds: [...prev, narrative.threshold],
            competitorAggressionUntilDay: (get().totalDays || 0) + 30,
          });
        }
      } catch (err) { console.warn('[coreSlice] marketScale narrative failed:', err); }
    }

    // 17/06/2026 (Lot 6 Phase 3) — Tick des événements narratifs Diplomatie
    // (OPA, fusion, plainte, débauchage, articles presse). Évalué tous les
    // jours mais avec probabilités quotidiennes faibles + cooldown long
    // pour éviter la noyade de boîte.
    if (typeof get().relationEventsTick === 'function') {
      try { get().relationEventsTick(); }
      catch (err) { console.warn('[coreSlice] relationEventsTick failed:', err); }
    }

    // 17/06/2026 (Lot 6 Phase L2) — Tick des procédures juridiques
    // (URSSAF, DREAL, prud'hommes, douanes, litige client). Mêmes
    // mécaniques que les événements narratifs mais signés par une
    // autorité (senderType='legal') et adoucis par Juliette si embauchée.
    if (typeof get().legalProceduresTick === 'function') {
      try { get().legalProceduresTick(); }
      catch (err) { console.warn('[coreSlice] legalProceduresTick failed:', err); }
    }

    // Push burnout-alert messages after the commit so drivers reference is
    // up-to-date (each push reads driverId from current state).
    burnoutAlerts.forEach(driverId => {
      const target = get().drivers.find(d => d.id === driverId);
      if (target) {
        get().pushMessageFromTemplate('drv-burnout-alert-01', {
          driverId,
          name: target.name,
        });
      }
    });

    // Push low-satisfaction renegotiation alerts after commit. Each message
    // carries the requested raise via msg.context so the counter-offer effect
    // (driverCounterOffer) knows what the candidate considers "fair".
    renegotiationAlerts.forEach(driverId => {
      const target = get().drivers.find(d => d.id === driverId);
      if (target) {
        get().pushMessageFromTemplate('drv-renegotiation-low-sat-01', {
          driverId,
          name: target.name,
          // The template body mentions +300 €/mo; carry the same in payload
          // so the driverCounterOffer effect knows the expected raise.
          payload: { expectedRaise: 300 },
        });
      }
    });

    // Monthly fiscal audit roll — gated by notoriety (>30). Probability ramps
    // with visibility; capped at 2 audits per fiscal year.
    if (newDay === 1 && typeof get().rollMonthlyAudit === 'function') {
      get().rollMonthlyAudit();
    }

    // Daily messagerie tick — expires unread decisions, purges old info
    // messages, rolls a random narrative message (~50 % chance).
    if (typeof get().tickMessages === 'function') {
      get().tickMessages();
    }

    // 04/02/2026 — Phase R1 : tick hebdomadaire du vivier RH dynamique
    // (arrivées/départs aléatoires + débauchage concurrent + email narratif).
    // Le slice gère lui-même son propre TICK_INTERVAL_DAYS = 7 et son
    // bookkeeping (lastTickDay), donc on peut l'appeler tous les jours sans
    // surcoût.
    if (typeof get().tickDriverPool === 'function') {
      try { get().tickDriverPool(); }
      catch (err) { console.warn('[driverPool] tick failed:', err); }
    }

    // 13/07/2026 — Équilibrage : pression concurrentielle sur les marges
    // > 50 %. Message pédagogique au déclenchement (front montant) +
    // notification de détente quand la marge redescend.
    if (marginPressure && marginPressure.active && !get().marginPressureActive) {
      set({ marginPressureActive: true });
      if (typeof get().pushCustomMessage === 'function') {
        // 31/07/2026 — cohérence : DAF si recruté, sinon conseiller/anonyme.
        const financeSender = getFinanceSender(get());
        get().pushCustomMessage({
          senderType: 'advisor',
          senderName: financeSender.senderName,
          subject: 'Vos marges attirent la concurrence',
          body: `Bonjour,\n\nNotre marge nette dépasse ${Math.round(marginPressure.margin * 100)} % sur les 30 derniers jours. Dans le transport routier réel, la marge nette moyenne est de 2 à 6 % : à ce niveau, nos clients comparent les prix et la concurrence casse les tarifs pour récupérer nos contrats.\n\nConséquence : les clients de nos CONTRATS CLASSIQUES renégocient — leurs revenus baissent progressivement tant que la marge reste au-dessus de 50 %.\n\n💡 À savoir : les contrats gagnés par APPEL D'OFFRES ne sont pas concernés — leurs prix sont verrouillés par contrat. Les AO sont plus difficiles à remporter, mais c'est la voie durable vers les marges élevées.\n\n${financeSender.signature}`,
          kind: 'warning',
        });
      }
    } else if (marginPressure && !marginPressure.active && get().marginPressureActive) {
      set({
        marginPressureActive: false,
        notifications: [...(get().notifications || []).slice(-30), {
          id: generateId(),
          type: 'info',
          title: 'Pression concurrentielle retombée',
          message: 'Votre marge nette est repassée sous 50 % : les clients ne renégocient plus vos contrats classiques.',
          day: get().totalDays,
          read: false,
        }],
      });
    }

    // 28/09/2026 — Plafond d'équilibre de marché (65 % de marge brute par
    // contrat, AO inclus) : message DAF au premier contrat renégocié,
    // notification de détente quand plus aucun contrat n'est sous tension.
    if (_equilibrium.pressuredCount > 0 && !get().marketEquilibriumActive) {
      set({ marketEquilibriumActive: true });
      if (typeof get().pushCustomMessage === 'function') {
        const financeSender = getFinanceSender(get());
        const capPct = Math.round(MARKET_EQUILIBRIUM_MAX_GROSS_MARGIN * 100);
        get().pushCustomMessage({
          senderType: 'advisor',
          senderName: financeSender.senderName,
          subject: 'Marché sous tension : nos clients renégocient',
          body: `Bonjour,\n\n${_equilibrium.pressuredCount} de nos contrats en cours dégage(nt) plus de ${capPct} % de marge brute réelle (bonus inclus). Sur un marché concurrentiel, aucun client n'accepte durablement de payer un tarif qui laisse une telle marge à son transporteur : il compare, met en concurrence et renégocie.\n\nConséquence : le tarif journalier de ces contrats — appels d'offres compris — baisse progressivement (−3 % par jour au maximum) jusqu'à revenir à ${capPct} % de marge brute. Le badge « Marché sous tension » sur le tableau de bord signale les contrats concernés, avec le tarif d'origine barré.\n\n💡 Pour continuer à croître, il faut désormais jouer le volume, la qualité de service et la maîtrise des coûts plutôt que l'empilement des bonus.\n\n${financeSender.signature}`,
          kind: 'warning',
        });
      }
    } else if (_equilibrium.pressuredCount === 0 && get().marketEquilibriumActive) {
      set({
        marketEquilibriumActive: false,
        notifications: [...(get().notifications || []).slice(-30), {
          id: generateId(),
          type: 'info',
          title: 'Marché détendu',
          message: 'Plus aucun contrat en cours n\'est renégocié : vos marges brutes sont revenues sous le plafond de marché.',
          day: get().totalDays,
          read: false,
        }],
      });
    }

    // 13/07/2026 — Arc « crédit véhicule » (événement confrère retraité) :
    // la réservation expire si le financement n'est pas conclu à temps.
    const _pvf = get().pendingVehicleFinancing;
    if (_pvf && (get().totalDays || 0) > _pvf.expiresDay) {
      set({
        pendingVehicleFinancing: null,
        notifications: [...(get().notifications || []).slice(-30), {
          id: generateId(),
          type: 'warning',
          title: 'Véhicule vendu à un autre acheteur',
          message: `Faute de financement dans les temps, le confrère a vendu son ${_pvf.brand || ''} ${_pvf.model || ''} à un autre transporteur. L'occasion est passée.`,
          day: get().totalDays,
          read: false,
        }],
      });
    }

    // 04/02/2026 — Phase Q1 : émission des messages decision CET fin d'année.
    // Au jour 350 de chaque année (mi-décembre), pour chaque chauffeur avec
    // jours de congés non posés, créer un message decision avec 2 choix :
    // (1) Paiement immédiat en bulletin de décembre ;
    // (2) Report sur Compte Épargne Temps (CET).
    // Réponse par défaut si non traité avant le 14 janvier = report CET.
    const _dayOfYear = ((get().totalDays || 0) % 365);
    if (_dayOfYear === 350 && typeof get().triggerEndOfYearCetChoice === 'function') {
      try { get().triggerEndOfYearCetChoice(); }
      catch (err) { console.warn('[CET] year-end trigger failed:', err); }
    }

    return true;
  },

  resetGame: () => {
    // Cross-slice reset: clear tax/bank/support state too. See createCompany
    // for the rationale.
    if (typeof get().resetTaxState === 'function') get().resetTaxState();
    // 10/07/2026 (P3 user) — Le glossaire pédagogique SURVIT au reset :
    // l'apprenant conserve les concepts déjà découverts entre deux parties.
    const preservedConcepts = get().discoveredConcepts || {};
    // 11/02/2026 — Default state payload extracted to helpers/resetGameState
    // (refactor coreSlice >2700 lignes).
    // 12/02/2026 (fix user prod « arrivé en avril les anciens chauffeurs
    // apparaissent ») — Reset nucléaire via pristine snapshot capturé
    // au bootstrap : garantit qu'AUCUN champ data hérité ne survit à
    // "Quitter la partie", même s'il n'est pas listé dans
    // `buildDefaultGameState()` (ex : `pendingMarchettiBonus`, `hiredSupport`,
    // `depotInvestments`, etc. définis dans leurs slices respectifs).
    const { getPristineStore } = require('../gameStorePristine');
    const pristine = getPristineStore();
    if (pristine) {
      const currentState = get();
      const cleared = {};
      // 1) Efface tous les champs data actuels (non-fonction) → undefined.
      for (const k of Object.keys(currentState)) {
        if (typeof currentState[k] !== 'function') cleared[k] = undefined;
      }
      set(cleared);
      // 2) Ré-applique la valeur pristine du snapshot (data-fields only).
      const pristineData = {};
      for (const k of Object.keys(pristine)) {
        if (typeof pristine[k] !== 'function') pristineData[k] = pristine[k];
      }
      set(pristineData);
    }
    // 3) Applique par-dessus le default state HST (config par défaut).
    set(buildDefaultGameState());
    // 4) Ré-applique le glossaire pédagogique préservé (P3 — 10/07/2026).
    set({ discoveredConcepts: preservedConcepts, pendingConceptToasts: [] });
  },

  /**
   * Increment the "NEW" badge counter for a given nav path. Used by the
   * support slice when a hire unlocks a feature on that page.
   */
  bumpNewFeatureBadge: (path) => {
    if (!path) return;
    set((s) => ({
      newFeatureCounts: {
        ...(s.newFeatureCounts || {}),
        [path]: ((s.newFeatureCounts || {})[path] || 0) + 1,
      },
    }));
  },

  /** Clear the "NEW" badge for a nav path once the user visits it. */
  markNavFeatureSeen: (path) => {
    if (!path) return;
    set((s) => {
      const next = { ...(s.newFeatureCounts || {}) };
      delete next[path];
      return { newFeatureCounts: next };
    });
  },

  /**
   * Pedagogical milestone trigger — bumps the "NEW" badge on `path` AT MOST
   * ONCE per game for a given milestone ID. Used by other slices to signal
   * one-time learning events (first M&A opportunity, first DREAL audit,
   * first strike, phase upgrade, etc.) without spamming the nav on every
   * recurrence.
   *
   * Returns true if the milestone was newly triggered, false if it was
   * already seen.
   */
  triggerMilestone: (id, path) => {
    if (!id || !path) return false;
    const state = get();
    const seen = state.milestonesSeen || [];
    if (seen.includes(id)) return false;
    set({
      milestonesSeen: [...seen, id],
      newFeatureCounts: {
        ...(state.newFeatureCounts || {}),
        [path]: ((state.newFeatureCounts || {})[path] || 0) + 1,
      },
    });
    return true;
  },
});
